import React, { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Package, RefreshCw, Send, Plus, Trash2, Upload, X, ChevronDown, ChevronUp, Loader2, Settings, FileText, AlertTriangle, CheckSquare, Square } from "lucide-react";
import DashboardLayout from "@/components/layout/DashboardLayout";
import api from "@/utils/api";
import { toast } from "sonner";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import DuplicateWarning, { useDuplicateCheck } from "@/components/shared/DuplicateWarning";

const DIVISORS = [5000, 4500, 4000, 2500];

const PINCODE_CITY_MAP = {
  "110001": "New Delhi", "110002": "New Delhi", "110003": "New Delhi", "110005": "New Delhi",
  "400001": "Mumbai", "400002": "Mumbai", "400003": "Mumbai", "400050": "Mumbai", "400051": "Mumbai",
  "560001": "Bangalore", "560002": "Bangalore", "560003": "Bangalore", "560004": "Bangalore",
  "600001": "Chennai", "600002": "Chennai", "600003": "Chennai", "600004": "Chennai",
  "700001": "Kolkata", "700002": "Kolkata", "700003": "Kolkata", "700004": "Kolkata",
  "500001": "Hyderabad", "500002": "Hyderabad", "500003": "Hyderabad", "500004": "Hyderabad",
  "411001": "Pune", "411002": "Pune", "411003": "Pune", "411004": "Pune",
  "380001": "Ahmedabad", "380002": "Ahmedabad", "380003": "Ahmedabad", "380004": "Ahmedabad",
  "302001": "Jaipur", "302002": "Jaipur", "302003": "Jaipur", "302004": "Jaipur",
  "226001": "Lucknow", "226002": "Lucknow", "226003": "Lucknow", "226004": "Lucknow",
  "440001": "Nagpur", "440002": "Nagpur", "440003": "Nagpur", "440004": "Nagpur",
  "682001": "Kochi", "682002": "Kochi", "682003": "Kochi", "682004": "Kochi",
  "641001": "Coimbatore", "641002": "Coimbatore", "641003": "Coimbatore",
  "530001": "Visakhapatnam", "530002": "Visakhapatnam", "530003": "Visakhapatnam",
  "520001": "Vijayawada", "520002": "Vijayawada", "520003": "Vijayawada",
  "516001": "Kadapa", "516002": "Kadapa", "516003": "Kadapa",
  "522001": "Guntur", "522002": "Guntur", "522003": "Guntur",
  "524001": "Nellore", "524002": "Nellore", "524003": "Nellore",
  "533001": "Rajahmundry", "533002": "Rajahmundry", "533003": "Rajahmundry",
  "534001": "Eluru", "534002": "Eluru",
  "517001": "Tirupati", "517002": "Tirupati",
  "515001": "Anantapur", "515002": "Anantapur",
  "518001": "Kurnool", "518002": "Kurnool",
};

const CITY_PINCODE_MAP = {
  "new delhi": "110001", "delhi": "110001",
  "mumbai": "400001", "bombay": "400001",
  "bangalore": "560001", "bengaluru": "560001",
  "chennai": "600001", "madras": "600001",
  "kolkata": "700001", "calcutta": "700001",
  "hyderabad": "500001",
  "pune": "411001",
  "ahmedabad": "380001",
  "jaipur": "302001",
  "lucknow": "226001",
  "nagpur": "440001",
  "kochi": "682001", "cochin": "682001",
  "coimbatore": "641001",
  "visakhapatnam": "530001", "vizag": "530001",
  "vijayawada": "520001",
  "kadapa": "516001", "cuddapah": "516001",
  "guntur": "522001",
  "nellore": "524001",
  "rajahmundry": "533001",
  "eluru": "534001",
  "tirupati": "517001",
  "anantapur": "515001",
  "kurnool": "518001",
};

function calcVolWt(l, b, h, qty, divisor) {
  const lv = parseFloat(l) || 0, bv = parseFloat(b) || 0,
    hv = parseFloat(h) || 0, qv = parseFloat(qty) || 1, dv = parseFloat(divisor) || 5000;
  if (!lv || !bv || !hv) return 0;
  return parseFloat(((lv * bv * hv * qv) / dv).toFixed(2));
}

const cleanPhone = (val) => {
  // Remove all non-digits
  let digits = val.replace(/\D/g, "");
  // Remove leading 91 if 12 digits starting with 91
  if (digits.length === 12 && digits.startsWith("91")) {
    digits = digits.slice(2);
  }
  // Remove leading 0 if 11 digits
  if (digits.length === 11 && digits.startsWith("0")) {
    digits = digits.slice(1);
  }
  // Cap at 10 digits
  return digits.slice(0, 10);
};

const Field = ({ label, children, required, error }) => (
  <div>
    <label className="block text-xs font-semibold text-slate-600 mb-1.5">
      {label}{required && <span className="text-red-500 ml-0.5">*</span>}
    </label>
    {children}
    {error && <p className="text-xs text-red-500 mt-1">{error}</p>}
  </div>
);

const inputCls = "w-full px-3 py-2 border border-slate-200 rounded-sm text-sm focus:outline-none focus:border-brand-500 focus:ring-1 focus:ring-brand-100 bg-white";
const dupCls = "w-full px-3 py-2 border border-red-400 rounded-sm text-sm focus:outline-none focus:border-red-500 bg-red-50";
const inputErrorCls = "w-full px-3 py-2 border border-red-300 rounded-sm text-sm focus:outline-none focus:border-red-500 focus:ring-1 focus:ring-red-100 bg-white";
const selectCls = "w-full px-3 py-2 border border-slate-200 rounded-sm text-sm focus:outline-none focus:border-brand-500 bg-white";

export default function BookingConsole() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [cloneWh, setCloneWh] = useState(null); // pickup location of a cloned shipment, applied once the customer's locations load
  const [offlineDraft, setOfflineDraft] = useState(null); // Offline Booking draft being completed (additive)
  const photoRef = useRef();
  const { user } = useAuth();
  // Partner-mode gating (Aug 31 2026, Stage B of Booking Console
  // unification, memory #13/#18): EXECUTION-type partners cannot book at
  // all -- matches the access-denied behavior already in PartnerBookings.jsx.
  // This check runs before any other hooks are affected since it's a plain
  // conditional render, not an early return -- rules-of-hooks are respected
  // by placing the actual early-return check further down, after all
  // useState/useEffect declarations (mirrors PartnerBookings.jsx's own
  // comment: "Gate EXECUTION partners (AFTER all hooks to respect
  // rules-of-hooks)").
  const isPartner = user?.role === "partner";
  const partnerType = user?.partner_type;

  // ── Account & Pickup Mode ──
  const [accountType, setAccountType] = useState("domestic");
  const [pickupMode, setPickupMode] = useState("schedule"); // "schedule" | "counter"

  // ── Warehouse / Pickup Location ──
  const [warehouse, setWarehouse] = useState("");
  const [warehouseList, setWarehouseList] = useState([]);
  const [showLocationWarning, setShowLocationWarning] = useState(false);
  const [pendingWarehouse, setPendingWarehouse] = useState("");
  const [showAddLocation, setShowAddLocation] = useState(false);
  const [newLocation, setNewLocation] = useState({
    name: "", address: "", city: "",
    pincode: "", contact_person: "",
    contact_phone: ""
  });
  const [savingLocation, setSavingLocation] = useState(false);

  // ── Pickup scheduling ──
  const [pickupDate, setPickupDate] = useState(0);
  const [pickupSlot, setPickupSlot] = useState("EVENING");
  const [pickupConfirmed, setPickupConfirmed] = useState(false);
  const [pickupContact, setPickupContact] = useState("");
  const [pickupPhone, setPickupPhone] = useState("");

  // ── FROM / TO ──
  const [fromCity, setFromCity] = useState("");
  const [fromPincode, setFromPincode] = useState("");
  const [toCity, setToCity] = useState("");
  const [toPincode, setToPincode] = useState("");
  const [fromPincodeError, setFromPincodeError] = useState("");
  const [toPincodeError, setToPincodeError] = useState("");

  // ── Sender ──
  const [senderName, setSenderName] = useState("");
  const [senderPhone, setSenderPhone] = useState("");
  const [senderAddress, setSenderAddress] = useState("");

  // ── Receiver ──
  const [receiverName, setReceiverName] = useState("");
  const [receiverPhone, setReceiverPhone] = useState("");
  const [receiverAddress, setReceiverAddress] = useState("");
  const [receiverIsBilling, setReceiverIsBilling] = useState(false);
  const [senderSameAsPickup, setSenderSameAsPickup] = useState(false);
  const [senderIsBilling, setSenderIsBilling] = useState(false); // Sender = customer's billing profile (name, phone, address)

  // ── Delivery Location (saved destination address book) ──
  const [deliveryLocation, setDeliveryLocation] = useState("");
  const [deliveryLocationList, setDeliveryLocationList] = useState([]);
  const [showAddDeliveryLocation, setShowAddDeliveryLocation] = useState(false);
  const [newDeliveryLocation, setNewDeliveryLocation] = useState({
    name: "", address: "", city: "",
    pincode: "", contact_person: "",
    contact_phone: ""
  });
  const [savingDeliveryLocation, setSavingDeliveryLocation] = useState(false);

  // ── Seller/Vendor ──
  const [showSeller, setShowSeller] = useState(false);
  const [sellerName, setSellerName] = useState("");
  const [sellerPhone, setSellerPhone] = useState("");
  const [sellerGST, setSellerGST] = useState("");
  const [sellerAddress, setSellerAddress] = useState("");

  // ── Shipment Details ──
  const [mode, setMode] = useState("surface");
  const [paymentMode, setPaymentMode] = useState("");
  const [paymentTiming, setPaymentTiming] = useState(""); // "now" | "ppp"
  const [consignmentType, setConsignmentType] = useState("non-document");
  const [materialDesc, setMaterialDesc] = useState("");
  const [consignmentValue, setConsignmentValue] = useState("");
  const [initialWeight, setInitialWeight] = useState("");
  const [codAmount, setCodAmount] = useState("");

  // ── Product & Service ──
  const [productCategory, setProductCategory] = useState("");
  const [connectionThrough, setConnectionThrough] = useState("");
  const [showC2cUpgradeWarning, setShowC2cUpgradeWarning] = useState(false);
  const [productCategories, setProductCategories] = useState([]);
  
  // ── Insurance ──
  const [insuranceType, setInsuranceType] = useState("OWNER_RISK");
  const insurancePremium = insuranceType === "CARRIER_RISK"
    ? Math.round((parseFloat(consignmentValue) || 0) * 0.005)
    : 0;

  // ── Eway Bill ──
  const [ewayBill, setEwayBill] = useState("");
  const [ewayFile, setEwayFile] = useState(null);
  const ewayRef = useRef(null);
  const requiresEwayBill = (parseFloat(consignmentValue) || 0) > 50000;

  // ── Weight & Dimensions ──
  const [boxMode, setBoxMode] = useState("single");
  const [addLater, setAddLater] = useState(false);
  const [deadWeight, setDeadWeight] = useState("");
  const [length, setLength] = useState("");
  const [breadth, setBreadth] = useState("");
  const [height, setHeight] = useState("");
  const [divisor, setDivisor] = useState(5000);
  const [boxes, setBoxes] = useState([{ id: 1, qty: 1, l: "", b: "", h: "", divisor: 4000, deadWt: "" }]);
  const [sameSize, setSameSize] = useState(false);
  const [sameSizeData, setSameSizeData] = useState({ l: "", b: "", h: "", divisor: 4000, deadWt: "" });
  const [totalBoxCount, setTotalBoxCount] = useState(1);

  // ── Photos ──
  const [photos, setPhotos] = useState([]);
  const [invoiceFile, setInvoiceFile] = useState(null);
  const [invoiceNumber, setInvoiceNumber] = useState("");
  const [qrModal, setQrModal] = useState(null);

  useEffect(() => {
    if (!qrModal?.linkId) return;
    const interval = setInterval(async () => {
      try {
        const statusRes = await api.get(`/v2/payments/links/${qrModal.linkId}/status`);
        if (statusRes.data.status === "PAID") {
          clearInterval(interval);
          toast.success("Payment received!");
          finishBooking(qrModal.orderId, qrModal.awbCount);
        }
      } catch (e) { /* transient errors — keep polling */ }
    }, 4000);
    return () => clearInterval(interval);
  }, [qrModal?.linkId]);

  
     const [invoiceMode, setInvoiceMode] = useState("");
     const invoiceRef = useRef(null);

  // ── Billing ──
  const [billOverride, setBillOverride] = useState("");
  const [estimatedPrice, setEstimatedPrice] = useState(null);
  const [rateDetails, setRateDetails] = useState(null);
  const [selectedVendor, setSelectedVendor] = useState(null);

  // ── Special Instructions ──
  const [specialInstructions, setSpecialInstructions] = useState("");

  // ── Customers ──
  const [customers, setCustomers] = useState([]);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [partnerCustomerName, setPartnerCustomerName] = useState(""); // additive: Booking Console Unification Step 3 (partner free-text customer)
  const [customerWallet, setCustomerWallet] = useState(null);

  useEffect(() => {
    if (!selectedCustomer) return;
    if (selectedCustomer.customer_segment === "WALKIN" || selectedCustomer.email?.endsWith("@walkin.sendtoo.in")) return; // staff picks UPI/Cash manually
    if (customerWallet?.credit_enabled) setPaymentMode("credit");
    else setPaymentMode("prepaid");
  }, [selectedCustomer, customerWallet]);
  const [defaultPickupLocationId, setDefaultPickupLocationId] = useState("");
  const [companyLocation, setCompanyLocation] = useState(null);
  const [customerSearch, setCustomerSearch] = useState("");
  const [showCustomerDropdown, setShowCustomerDropdown] = useState(false);
  const [showAddCustomer, setShowAddCustomer] = useState(false);
  const [showWalkinCustomer, setShowWalkinCustomer] = useState(false);
  const [walkinCustomer, setWalkinCustomer] = useState({ name: "", mobile: "", address: "", pincode: "" });
  const [bookingGroupId, setBookingGroupId] = useState(null);
  const [bookingSuccess, setBookingSuccess] = useState(null); // { orderId, awbCount }
  const [savingWalkin, setSavingWalkin] = useState(false);
  const [newCustomer, setNewCustomer] = useState({ name: "", email: "", phone: "", password: "Customer@123" });
  // Duplicate gate for both inline customer forms: "Add Customer" must be clean; the walk-in form reuses an existing mobile.
  const dupNew = useDuplicateCheck({ name: newCustomer.name, mobile: newCustomer.phone, email: newCustomer.email });
  const dupWalkin = useDuplicateCheck({ name: walkinCustomer.name, mobile: walkinCustomer.mobile });
  const [savingCustomer, setSavingCustomer] = useState(false);

  // ── Order ID ──
  const [orderId, setOrderId] = useState("");
  const [fetchingOrderId, setFetchingOrderId] = useState(true);
  const [loading, setLoading] = useState(false);

  // ── AWB count ──
  const totalAwbCount = boxMode === "multi"
    ? sameSize
      ? totalBoxCount
      : boxes.reduce((sum, b) => sum + (parseInt(b.qty) || 1), 0)
    : 1;

  useEffect(() => {
    fetchCustomers();
    fetchWarehouses();
    fetchNextOrderId();
    fetchProductCategories();
    fetchCompanyLocation();
  }, []);

  const fetchNextOrderId = async () => {
    try {
      const res = await api.get("/shipments/next-order-id");
      setOrderId(res.data.next_order_id);
    } catch { setOrderId("--"); }
    finally { setFetchingOrderId(false); }
  };

  const finishBooking = (orderId, awbCount) => {
    setQrModal(null);
    setBookingSuccess({ orderId, awbCount });
  };

  const resetForNextShipment = () => {
    setBookingSuccess(null);
    // Receiver
    setReceiverName(""); setReceiverPhone(""); setReceiverAddress(""); setReceiverIsBilling(false);
    setToCity(""); setToPincode(""); setToPincodeError("");
    // Seller / vendor
    setShowSeller(false); setSellerName(""); setSellerPhone(""); setSellerGST(""); setSellerAddress("");
    // Consignment
    setConsignmentType("non-document"); setMaterialDesc(""); setConsignmentValue("");
    setInitialWeight(""); setCodAmount("");
    setProductCategory(""); setConnectionThrough("");
    setInsuranceType("OWNER_RISK");
    setEwayBill("");
    // Boxes / weight
    setBoxMode("single"); setAddLater(false);
    setDeadWeight(""); setLength(""); setBreadth(""); setHeight(""); setDivisor(5000);
    setBoxes([{ id: 1, qty: 1, l: "", b: "", h: "", divisor: 4000, deadWt: "" }]);
    setSameSize(false); setSameSizeData({ l: "", b: "", h: "", divisor: 4000, deadWt: "" });
    setTotalBoxCount(1);
    // Documents
    setPhotos([]); setInvoiceFile(null); setInvoiceMode(""); setInvoiceNumber(""); setEwayFile(null);
    // Pricing
    setBillOverride(""); setEstimatedPrice(null); setRateDetails(null); setSelectedVendor(null);
    // Misc
    setSpecialInstructions("");
    // Pickup logistics — re-confirm each time even if same date/slot
    setPickupConfirmed(false);
    // Fresh order ID for the next shipment
    setFetchingOrderId(true);
    fetchNextOrderId();
  };

  const fetchCustomers = async (q = "") => {
    try {
      const params = { role: "customer" };
      if (q) params.search = q;
      const res = await api.get("/users", { params });
      setCustomers(res.data || []);
    } catch { }
  };

  const fetchWarehouses = async () => {
    try {
      const res = await api.get("/warehouses");
      const activeWarehouses = (res.data || []).filter(w => w.is_active !== false);

      let companyLocations = [];
      try {
        const companyRes = await api.get("/v2/pickup-locations/list", { params: { owner_type: "COMPANY" } });
        companyLocations = (companyRes.data || [])
          .filter(l => l.is_active !== false)
          .map(l => ({
            id: l.location_code,
            code: l.location_code,
            name: l.location_name,
            address: [l.address_line1, l.address_line2].filter(Boolean).join(", "),
            city: l.city,
            pincode: l.pincode,
            contact_person: l.contact_person,
            contact_phone: l.contact_mobile,
            is_active: l.is_active,
          }));
      } catch { }

      setWarehouseList([...activeWarehouses, ...companyLocations]);
    } catch { }
  };
  const fetchCompanyLocation = async () => {
    try {
      const res = await api.get(`/v2/pickup-locations/list`, { params: { owner_type: "COMPANY" } });
      const list = res.data || [];
      const def = list.find(l => l.is_default) || list[0];
      if (def) {
        setCompanyLocation({
          name: def.location_name,
          address: def.address_line1,
          city: def.city,
          pincode: def.pincode,
          contact_phone: def.contact_mobile,
        });
      }
    } catch { }
  };

  const fetchCustomerWallet = async (customerId) => {
       try {
         const res = await api.get(`/v2/wallets/${customerId}/summary`);
         setCustomerWallet({
           balance: res.data.current_balance || 0,
           credit_enabled: res.data.wallet_credit_enabled || false,
           credit_limit: res.data.credit_limit || 0,
         });
       } catch {
         setCustomerWallet(null);
       }
     };const fetchCustomerDefaultLocation = async (customerId) => {
    try {
      const res = await api.get(`/v2/customers/profile/${customerId}`);
      setDefaultPickupLocationId(res.data.default_pickup_location_id || "");
    } catch {
      setDefaultPickupLocationId("");
    }
  };

  const fetchCustomerPickupLocations = async (customerId) => {
    try {
      const res = await api.get(`/v2/pickup-locations/list`, {
        params: { owner_id: customerId, owner_type: "CUSTOMER" }
      });
      // Real bug fix (Aug 25 2026): this used to REPLACE warehouseList
      // entirely with just this customer's own saved locations, silently
      // dropping the company location (e.g. 100016) from the dropdown the
      // moment any customer with a saved location was selected. Company
      // locations must always stay visible by default, per protocol --
      // now merges the customer's own locations on top of the existing
      // company list instead of replacing it.
      const customerLocs = (res.data || []).map(p => ({
        id: p.location_code,
        code: p.location_code || "LOC",
        name: p.location_name,
        address: [p.address_line1, p.address_line2].filter(Boolean).join(", "),
        city: p.city,
        pincode: p.pincode,
        contact_person: p.contact_person || "",
        contact_phone: p.contact_mobile || "",
        customer_id: customerId
      }));
      await fetchWarehouses();
      if (customerLocs.length > 0) {
        setWarehouseList((prev) => [...customerLocs, ...prev]);
      }
    } catch {
      fetchWarehouses();
    }
  };

  const fetchCustomerDeliveryLocations = async (customerId) => {
    try {
      const res = await api.get(`/v2/delivery-locations/list`, {
        params: { owner_id: customerId, owner_type: "CUSTOMER" }
      });
      const locs = (res.data || []).map(p => ({
        id: p.location_code,
        code: p.location_code || "LOC",
        name: p.location_name,
        address: [p.address_line1, p.address_line2].filter(Boolean).join(", "),
        city: p.city,
        pincode: p.pincode,
        contact_person: p.contact_person || "",
        contact_phone: p.contact_mobile || "",
      }));
      setDeliveryLocationList(locs);
    } catch {
      setDeliveryLocationList([]);
    }
  };

  const fetchProductCategories = async () => {
    try {
      const res = await api.get("/v2/products/categories");
      setProductCategories(res.data || []);
    } catch { }
  };

  // "Use this customer" from the duplicate banner: select the existing account instead of creating another.
  const selectExistingCustomer = (m) => {
    const c = { id: m.id, name: m.name || m.company, email: m.email, phone: m.mobile, customer_code: m.customer_code };
    setSelectedCustomer(c);
    fetchCustomerPickupLocations(c.id); fetchCustomerDeliveryLocations(c.id); fetchCustomerDefaultLocation(c.id); fetchCustomerWallet(c.id);
    setCustomerSearch(""); setShowCustomerDropdown(false); setShowAddLocation(false);
    setShowAddCustomer(false); setShowWalkinCustomer(false);
    toast.success("Existing customer selected");
  };

  const handleAddCustomer = async () => {
    if (!newCustomer.name || !newCustomer.email || !newCustomer.phone) {
      toast.error("Please fill Name, Email and Phone");
      return;
    }
    if (dupNew.hard) { toast.error("This customer already exists (same mobile or email). Use the existing customer."); return; }
    if (dupNew.blocked) { toast.error("A similar customer exists. Tick the confirmation if this is a different customer."); return; }
    setSavingCustomer(true);
    try {
      // Path B: POST /api/v2/customers/create (v2 field names). Quick-add
      // omits KYC; admin will complete via /admin/customers/new later.
      const v2Payload = {
        customer_type: "retail",
        customer_segment: "WALLET",
        contact_person: newCustomer.name,
        mobile: newCustomer.phone,
        email: newCustomer.email,
        password: newCustomer.password || "Customer@123",
        confirm_duplicate: !!dupNew.ack,
      };
      const res = await api.post("/v2/customers/create", v2Payload);
      if (res.data?.reused_existing) {
        toast.error("A customer with this mobile already exists \u2014 nothing new was created. Selecting the existing customer.");
        selectExistingCustomer({ id: res.data.customer_id || res.data.id, name: newCustomer.name, email: res.data.email, mobile: newCustomer.phone });
        return;
      }
      toast.success("Customer created!");
      const created = {
        id: res.data.id || res.data._id || res.data.customer_id,
        name: newCustomer.name,
        email: newCustomer.email,
        phone: newCustomer.phone
      };
      setSelectedCustomer(created);
      setCustomerSearch("");
      setShowCustomerDropdown(false);
      setShowAddCustomer(false);
      setNewCustomer({ name: "", email: "", phone: "", password: "Customer@123" });
      fetchCustomers();
    } catch (err) {
      const det = err.response?.data?.detail;
      toast.error(det && typeof det === "object" ? `${det.message || "Similar customer exists"}. Tick the confirmation and save again.` : (det || "Failed to create customer"));
    } finally {
      setSavingCustomer(false);
    }
  };
  const handleAddWalkinCustomer = async () => {
    if (!walkinCustomer.name || !walkinCustomer.mobile || !walkinCustomer.address || !walkinCustomer.pincode || walkinCustomer.pincode.length !== 6) {
      toast.error("Please fill Name, Mobile, Address and a valid 6-digit Pincode");
      return;
    }
    if (!dupWalkin.hard && dupWalkin.blocked) { toast.error("A similar customer exists. Tick the confirmation if this is a different customer."); return; }
    setSavingWalkin(true);
    try {
      // Check if this mobile is already a real customer — reuse instead of
      // failing on a duplicate-email attempt if staff re-enters someone.
      const existingRes = await api.get("/users", { params: { role: "customer", search: walkinCustomer.mobile } });
      const existingMatch = (existingRes.data || []).find(c => c.phone === walkinCustomer.mobile);
      if (existingMatch) {
        toast.success("Existing customer found — selected");
        setSelectedCustomer({
          id: existingMatch.id || existingMatch._id,
          name: existingMatch.name,
          email: existingMatch.email,
          phone: existingMatch.phone,
        });
        setSenderName(walkinCustomer.name);
        setSenderPhone(walkinCustomer.mobile);
        setSenderAddress(walkinCustomer.address);
        // Real gap fix (Sep 23 2026): the walk-in flow set sender
        // name/phone/address but never origin pincode/city, since the
        // walk-in form previously had no pincode field at all -- staff had
        // to separately type the pincode every time, even though it's
        // implied by the address they just entered. Now set alongside the
        // rest, matching how the saved-address dropdown already does it.
        // Reuses the same real endpoint + PINCODE_CITY_MAP fallback the
        // manual pincode-typing path already relies on (was guessed wrong
        // as a nonexistent /pincode-lookup/ endpoint in an earlier pass of
        // this same fix -- corrected against the real handleFromPincodeChange
        // implementation instead of re-guessing).
        handleFromPincodeChange(walkinCustomer.pincode);
        setShowWalkinCustomer(false);
        setWalkinCustomer({ name: "", mobile: "", address: "", pincode: "" });
        fetchCustomerWallet(existingMatch.id || existingMatch._id);
        setSavingWalkin(false);
        return;
      }

      // Real bug fix (Sep 5 2026): this used to build the walk-in email
      // directly here from the raw, unnormalized mobile input, bypassing
      // the backend's Aug 24 2026 phone-normalization fix entirely (that
      // fix only runs when `email` is left blank for the backend to
      // auto-generate) -- silently reintroduced the exact same class of
      // bug (a space/formatting artifact in the phone baked into the
      // email, breaking Cashfree payment links). Now leaves email blank
      // and lets the backend build it from a properly normalized mobile.
      const autoPassword = "Walkin@" + Math.random().toString(36).slice(-6);
      const v2Payload = {
        customer_type: "retail",
        customer_segment: "WALKIN",
        contact_person: walkinCustomer.name,
        mobile: walkinCustomer.mobile,
        password: autoPassword,
        business_address: walkinCustomer.address,
      };
      const res = await api.post("/v2/customers/create", v2Payload);
      toast.success("Walk-in customer created!");
      const created = {
        id: res.data.id || res.data._id || res.data.customer_id,
        name: walkinCustomer.name,
        // Real email now comes from the backend's own (correctly
        // normalized) generation -- no longer guessed/rebuilt here.
        email: res.data.email || "",
        phone: walkinCustomer.mobile,
      };
      setSelectedCustomer(created);
      setSenderName(walkinCustomer.name);
      setSenderPhone(walkinCustomer.mobile);
      setSenderAddress(walkinCustomer.address);
      handleFromPincodeChange(walkinCustomer.pincode);
      setShowWalkinCustomer(false);
      setWalkinCustomer({ name: "", mobile: "", address: "", pincode: "" });
      fetchCustomerWallet(created.id);
      fetchCustomers();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to create walk-in customer");
    } finally {
      setSavingWalkin(false);
    }
  };

  const filteredCustomers = customers.filter(c => {
    const q = customerSearch.toLowerCase();
    return c.name?.toLowerCase().includes(q) || c.customer_code?.includes(q) || c.phone?.includes(q) || c.email?.toLowerCase().includes(q);
  });

  const isValidPincode = (pin) => /^\d{6}$/.test(pin);

  const handleFromPincodeChange = (value) => {
    const cleaned = value.replace(/\D/g, "").slice(0, 6);
    setFromPincode(cleaned);
    if (cleaned.length === 6) {
      setFromPincodeError("");
      api.get(`/v2/pincode-master/lookup/${cleaned}`)
        .then(r => { if (r.data?.city) setFromCity(r.data.city); })
        .catch(() => {
          const city = PINCODE_CITY_MAP[cleaned];
          if (city) setFromCity(city);
        });
    } else if (cleaned.length > 0) {
      setFromPincodeError("Pincode must be 6 digits");
    } else {
      setFromPincodeError("");
    }
  };

  const handleToPincodeChange = (value) => {
    const cleaned = value.replace(/\D/g, "").slice(0, 6);
    setToPincode(cleaned);
    if (cleaned.length === 6) {
      setToPincodeError("");
      api.get(`/v2/pincode-master/lookup/${cleaned}`)
        .then(r => { if (r.data?.city) setToCity(r.data.city); })
        .catch(() => {
          const city = PINCODE_CITY_MAP[cleaned];
          if (city) setToCity(city);
        });
    } else if (cleaned.length > 0) {
      setToPincodeError("Pincode must be 6 digits");
    } else {
      setToPincodeError("");
    }
  };

  const handleFromCityChange = (value) => {
    setFromCity(value);
    const pincode = CITY_PINCODE_MAP[value.toLowerCase().trim()];
    if (pincode) { setFromPincode(pincode); setFromPincodeError(""); }
  };

  const handleToCityChange = (value) => {
    setToCity(value);
    const pincode = CITY_PINCODE_MAP[value.toLowerCase().trim()];
    if (pincode) { setToPincode(pincode); setToPincodeError(""); }
  };

  // Weight calculations
  const singleVolWt = calcVolWt(length, breadth, height, 1, divisor);
  const singleDeadWt = parseFloat(deadWeight) || 0;
  const singleChargeableWt = Math.max(singleDeadWt, singleVolWt);
  const multiTotalDeadWt = boxes.reduce((s, b) => s + ((parseFloat(b.deadWt) || 0) * (parseInt(b.qty) || 1)), 0);
  const multiTotalVolWt = boxes.reduce((s, b) => s + calcVolWt(b.l, b.b, b.h, b.qty, b.divisor), 0);
  const multiChargeableWt = Math.max(multiTotalDeadWt, multiTotalVolWt);

  // Same-size multi box weight
  const sameSizeVolWt = calcVolWt(sameSizeData.l, sameSizeData.b, sameSizeData.h, totalBoxCount, sameSizeData.divisor);
  const sameSizeDeadWt = (parseFloat(sameSizeData.deadWt) || 0) * totalBoxCount;
  const sameSizeChargeableWt = Math.max(sameSizeDeadWt, sameSizeVolWt);

  const finalChargeableWt = addLater
    ? (parseFloat(initialWeight) || 0)
    : boxMode === "single"
      ? singleChargeableWt
      : sameSize
        ? sameSizeChargeableWt
        : multiChargeableWt;

  // Delhivery C2C 3PL box/weight limits (Aug 19, 2026) -- Delhivery's
  // multi-AWB C2C API caps out at 5 boxes per shipment, and staff shouldn't
  // be routing genuinely large freight through C2C at all. Silently
  // auto-corrects the two cases with no valid C2C path (>5 boxes, >50kg);
  // shows an interactive choice only for the true judgment-call zone
  // (20-50kg, <=5 boxes), where either C2C or B2B is a legitimate option.
  useEffect(() => {
    if (connectionThrough !== "DELHIVERY_C2C") return;
    if (totalBoxCount > 5) {
      setConnectionThrough("DELHIVERY_B2B");
      toast.error("Delhivery C2C supports a maximum of 5 boxes per shipment — switched to Delhivery B2B.");
      return;
    }
    if (finalChargeableWt > 50) {
      setConnectionThrough("DELHIVERY_B2B");
      toast.error("Shipments over 50kg cannot go via Delhivery C2C — switched to Delhivery B2B.");
      return;
    }
    if (finalChargeableWt >= 20) {
      setShowC2cUpgradeWarning(true);
    }
  }, [connectionThrough, totalBoxCount, finalChargeableWt]);

  // Rate calculation
  const calculatePrice = async () => {
    if (!finalChargeableWt || !fromPincode || !toPincode) return;
    try {
      const res = await api.post("/v2/rate-calculator/calculate", {
        origin_pincode: fromPincode,
        destination_pincode: toPincode,
        weight_kg: finalChargeableWt,
        service_type: mode === "air" ? "EXPRESS" : "SURFACE",
        customer_type: accountType === "b2b" ? "B2B" : "B2C",
        customer_id: selectedCustomer?.id || null,
      });
      setRateDetails(res.data);
      setSelectedVendor(null);
      setEstimatedPrice(res.data.customer_price || res.data.vendor_cost || null);
    } catch {
      setRateDetails(null);
      try {
        const res = await api.post("/pricing/calculate", { weight: finalChargeableWt, origin_pincode: fromPincode, destination_pincode: toPincode });
        setEstimatedPrice(res.data.price);
      } catch { setEstimatedPrice(null); }
    }
  };

  useEffect(() => {
    if (finalChargeableWt && fromPincode && toPincode) calculatePrice();
  }, [finalChargeableWt, fromPincode, toPincode]);

  // Does the destination pincode agree with the city and with any pincode/town typed in the address? (wrong address => LR + e-way bill to cancel)
  const [addrIssues, setAddrIssues] = useState([]);
  useEffect(() => {
    if (!/^\d{6}$/.test(toPincode)) { setAddrIssues([]); return undefined; }
    const t = setTimeout(() => {
      api.post("/v2/manifest-gate/address-check", { pincode: toPincode, city: toCity, address: receiverAddress, label: "Delivery" })
        .then((r) => setAddrIssues(r.data?.issues || []))
        .catch(() => setAddrIssues([]));
    }, 600);
    return () => clearTimeout(t);
  }, [toPincode, toCity, receiverAddress]);

  const addBox = () => setBoxes([...boxes, { id: Date.now(), qty: 1, l: "", b: "", h: "", divisor: 4000, deadWt: "" }]);
  const updateBox = (id, field, value) => setBoxes(boxes.map(b => b.id === id ? { ...b, [field]: value } : b));
  const removeBox = (id) => { if (boxes.length > 1) setBoxes(boxes.filter(b => b.id !== id)); };
  useEffect(() => {
    if (defaultPickupLocationId && warehouseList.some(w => w.id === defaultPickupLocationId)) {
      setWarehouse(defaultPickupLocationId);
    }
  }, [defaultPickupLocationId, warehouseList]);

  useEffect(() => {
    const wh = warehouseList.find(w => w.id === warehouse);
    if (wh) {
      setSellerName(wh.name || "");
      setSellerPhone(wh.contact_phone || "");
      setSellerAddress(`${wh.address || ""}, ${wh.city || ""} - ${wh.pincode || ""}`);
    }
  }, [warehouse, warehouseList]);

  // Sender = Billing Account: fill sender from the customer's profile (company/contact, mobile, business address).
  useEffect(() => {
    if (!senderIsBilling || !selectedCustomer?.id) return;
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get(`/v2/customer-360/${selectedCustomer.id}`);
        if (cancelled) return;
        const p = data.profile || {}, u = data.user || {};
        const name = p.company_name || u.company_name || p.contact_person || u.name || "";
        const phone = String(p.mobile || u.phone || "").replace(/\D/g, "").slice(-10);
        const addr = [p.business_address || [p.address_line1, p.address_line2].filter(Boolean).join(", "), p.city, p.state].filter(Boolean).join(", ")
          + (p.pincode ? ` - ${p.pincode}` : "");
        if (!name && !addr) { toast.error("This customer has no billing details saved yet. Fill them in on the customer page first."); setSenderIsBilling(false); return; }
        setSenderName(name); setSenderPhone(phone); setSenderAddress(addr.trim());
        const pin = (p.pincode || (addr.match(/(?<!\d)\d{6}(?!\d)/) || [""])[0] || "");
        // In "schedule" mode the ORIGIN must be the pickup warehouse (Delhivery routes by exact pincode), so the customer's own
        // billing pincode is only used for origin at the counter, where there is no warehouse.
        if (pickupMode === "counter" && /^\d{6}$/.test(pin)) { try { handleFromPincodeChange(pin); } catch { setFromPincode(pin); } }
        toast.success("Sender filled from the customer's billing profile");
      } catch { toast.error("Could not load the customer's billing details"); setSenderIsBilling(false); }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [senderIsBilling, selectedCustomer?.id, pickupMode]);

  // In schedule mode the origin pincode/city always follow the selected pickup location (fixes "Pickup location mismatch" when
  // something else - billing profile, an Excel draft, a typed value - had put the customer's own pincode in Origin).
  useEffect(() => {
    if (pickupMode !== "schedule" || !warehouse) return;
    const wh = warehouseList.find((w) => w.id === warehouse);
    if (wh?.pincode && fromPincode !== wh.pincode) {
      setFromPincode(wh.pincode); setFromPincodeError("");
      if (wh.city) setFromCity(wh.city);
      toast.info(`Origin set to the pickup location (${wh.city || ""} ${wh.pincode}) - Delhivery needs the pickup warehouse pincode.`);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [warehouse, warehouseList, pickupMode]);

  // switching customer clears the toggle so a previous customer's details are never reused
  useEffect(() => { setSenderIsBilling(false); }, [selectedCustomer?.id]);

  useEffect(() => {
    if (receiverIsBilling || senderIsBilling) return;
    const defLoc = warehouseList.find(w => w.id === defaultPickupLocationId);
    if (defLoc) {
      setSenderName(defLoc.name || "");
      setSenderPhone((defLoc.contact_phone || "").replace(/\D/g, "").slice(0, 10));
      setSenderAddress(`${defLoc.address || ""}, ${defLoc.city || ""} - ${defLoc.pincode || ""}`);
    }
  }, [defaultPickupLocationId, warehouseList, pickupMode, receiverIsBilling, senderIsBilling]);

  useEffect(() => {
    if (!receiverIsBilling) return;
    const defLoc = warehouseList.find(w => w.id === defaultPickupLocationId);
    if (defLoc) {
      setReceiverName(defLoc.name || "");
      setReceiverPhone((defLoc.contact_phone || "").replace(/\D/g, "").slice(0, 10));
      setReceiverAddress(`${defLoc.address || ""}, ${defLoc.city || ""} - ${defLoc.pincode || ""}`);
    }
  }, [receiverIsBilling, defaultPickupLocationId, warehouseList]);

  useEffect(() => {
    if (!receiverIsBilling) { setSenderSameAsPickup(false); return; }
    setSenderName(""); setSenderPhone(""); setSenderAddress("");
  }, [receiverIsBilling]);

  useEffect(() => {
    if (!receiverIsBilling || !senderSameAsPickup) return;
    const wh = warehouseList.find(w => w.id === warehouse);
    if (wh) {
      setSenderName(wh.name || "");
      setSenderPhone((wh.contact_phone || "").replace(/\D/g, "").slice(0, 10));
      setSenderAddress(`${wh.address || ""}, ${wh.city || ""} - ${wh.pincode || ""}`);
    }
  }, [receiverIsBilling, senderSameAsPickup, warehouse, warehouseList]);


    const handlePhotoUpload = (e) => {
    const files = Array.from(e.target.files);
    setPhotos([...photos, ...files.map(f => ({ name: f.name, url: URL.createObjectURL(f), file: f }))]);
  };

  const finalBillAmount = billOverride ? parseFloat(billOverride) : estimatedPrice;

  const handleReset = () => {
    setSenderName(""); setSenderPhone(""); setSenderAddress("");
    setReceiverName(""); setReceiverPhone(""); setReceiverAddress("");
    setFromCity(""); setFromPincode(""); setToCity(""); setToPincode("");
    setFromPincodeError(""); setToPincodeError("");
    setMaterialDesc(""); setConsignmentValue(""); setCodAmount(""); setInitialWeight("");
    setDeadWeight(""); setLength(""); setBreadth(""); setHeight(""); setDivisor(5000);
    setBoxes([{ id: 1, qty: 1, l: "", b: "", h: "", divisor: 4000, deadWt: "" }]);
    setPhotos([]); setBillOverride(""); setSpecialInstructions("");
    setSelectedCustomer(null); setCustomerWallet(null); setEstimatedPrice(null); setRateDetails(null);
    setWarehouse(""); setPickupDate(0); setPickupSlot("EVENING");
    setPickupContact(""); setPickupPhone("");
    setInsuranceType("OWNER_RISK"); setEwayBill("");
    setProductCategory("");
    setSameSize(false); setTotalBoxCount(1);
    toast.info("Form reset.");
  };

  // ── Offline Booking draft prefill (additive): /staff/book?draft=<id> ─────────
  // Loads a draft created from the Excel form and fills what the form had. Nothing is booked
  // until staff review and press the normal submit button.
  useEffect(() => {
    const draftId = searchParams.get("draft");
    if (!draftId) return;
    (async () => {
      try {
        const { data: dr } = await api.get(`/v2/offline-bookings/drafts/${draftId}`);
        if (dr.status !== "DRAFT") { toast.error(`This draft is already ${dr.status.toLowerCase()}.`); return; }
        const d = dr.data || {};
        const cu = await api.get("/users", { params: { role: "customer", search: dr.customer_phone } });
        const cust = (cu.data || []).find((u) => u.id === dr.customer_id);
        if (cust) {
          setSelectedCustomer(cust);
          fetchCustomerPickupLocations(cust.id); fetchCustomerDeliveryLocations(cust.id);
          fetchCustomerDefaultLocation(cust.id); fetchCustomerWallet(cust.id);
        }
        setSenderName(d.sender_name || ""); setSenderPhone(d.sender_phone || ""); setSenderAddress(d.sender_address || "");
        setReceiverName(d.receiver_name || ""); setReceiverPhone(d.receiver_phone || ""); setReceiverAddress(d.receiver_address || "");
        if (pickupMode === "counter") { setFromCity(d.sender_city || ""); setFromPincode(d.sender_pincode || ""); } // schedule mode: origin follows the pickup location chosen below
        setToCity(d.receiver_city || ""); setToPincode(d.receiver_pincode || "");
        if (d.mode) setMode(d.mode);
        if (d.consignment_type) setConsignmentType(d.consignment_type);
        setMaterialDesc(d.contents || "");
        if (d.goods_value) setConsignmentValue(String(d.goods_value));
        if (d.eway_bill) setEwayBill(d.eway_bill);
        if ((d.requested_payment_mode || "").toLowerCase() === "cod") { setPaymentMode("cod"); if (d.cod_amount) setCodAmount(String(d.cod_amount)); }
        if (d.insurance_type) setInsuranceType(d.insurance_type);
        const n = d.boxes || 1;
        if (d.box_lines && d.box_lines.length) {
          setBoxMode("multi"); setSameSize(false);
          setBoxes(d.box_lines.map((x, i) => ({ id: i + 1, qty: x.qty, l: String(x.l), b: String(x.b), h: String(x.h), divisor: d.volumetric_divisor || 4000, deadWt: x.weight ? String(x.weight) : "" })));
        } else if (n > 1) {
          setBoxMode("multi"); setSameSize(true); setTotalBoxCount(n);
          setSameSizeData({ l: d.length ? String(d.length) : "", b: d.breadth ? String(d.breadth) : "", h: d.height ? String(d.height) : "",
                            divisor: d.volumetric_divisor || 4000, deadWt: d.weight ? String((d.weight / n).toFixed(2)) : "" });
        } else {
          setBoxMode("single");
          if (d.weight) setDeadWeight(String(d.weight));
          if (d.length) setLength(String(d.length));
          if (d.breadth) setBreadth(String(d.breadth));
          if (d.height) setHeight(String(d.height));
        }
        const notes = [d.special_instructions, d.reference && `Customer ref: ${d.reference}`, d.invoice_no && `Invoice: ${d.invoice_no}`,
                       d.fragile && "FRAGILE", d.packaging && `Packing: ${d.packaging}`, d.receiver_landmark && `Landmark: ${d.receiver_landmark}`,
                       d.pickup_date && `Requested pickup: ${d.pickup_date} ${d.pickup_time_pref || ""}`].filter(Boolean).join(" | ");
        setSpecialInstructions(notes);
        setOfflineDraft(dr);
        const miss = dr.missing || [];
        toast.success(`Draft loaded from Excel (${dr.customer_name}).` + (miss.length ? ` Still needed: ${miss.join(", ")}` : " All key details present - review and book."), { duration: 9000 });
      } catch (e) {
        toast.error(e?.response?.data?.detail || "Could not load the draft");
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  useEffect(() => {
    if (!cloneWh) return;
    const w = (warehouseList || []).find((x) => x.code === cloneWh || x.id === cloneWh);
    if (w) { setWarehouse(w.id); setCloneWh(null); }
  }, [cloneWh, warehouseList]);

  // ── Clone a (cancelled) shipment: /admin/book?clone=<shipment_id> ────────────
  // Pre-fills this form from the old shipment. Nothing is booked until the normal submit; booking creates a NEW order ID.
  useEffect(() => {
    const cloneId = searchParams.get("clone");
    if (!cloneId) return;
    (async () => {
      try {
        const { data: sh } = await api.get(`/shipments/${cloneId}`);
        let cust = null;
        try {
          const cu = await api.get("/users", { params: { role: "customer", search: sh.customer_name || sh.customer_phone || "" } });
          cust = (cu.data || []).find((u) => u.id === sh.customer_id) || null;
        } catch { /* customer picked by hand below */ }
        if (cust) {
          setSelectedCustomer(cust);
          fetchCustomerPickupLocations(cust.id); fetchCustomerDeliveryLocations(cust.id);
          fetchCustomerDefaultLocation(cust.id); fetchCustomerWallet(cust.id);
        }
        const o = sh.origin || {}, d = sh.destination || {};
        setSenderName(o.name || ""); setSenderPhone(o.phone || ""); setSenderAddress(o.sender_address || o.address || "");
        setFromCity(o.city || ""); setFromPincode(o.pincode || "");
        setReceiverName(d.name || ""); setReceiverPhone(d.phone || ""); setReceiverAddress(d.address || "");
        setToCity(d.city || ""); setToPincode(d.pincode || "");
        const lvl = String(sh.service_level || "").toUpperCase();
        setMode(lvl === "EXPRESS" ? "express" : lvl === "NDD" ? "ndd" : "surface");
        if (sh.package?.type) setConsignmentType(sh.package.type);
        setMaterialDesc(sh.package?.description || "");
        if (sh.consignment_value) setConsignmentValue(String(sh.consignment_value));
        if (sh.eway_bill_number) setEwayBill(sh.eway_bill_number);
        if (sh.insurance_type) setInsuranceType(sh.insurance_type);
        if (sh.special_instructions) setSpecialInstructions(sh.special_instructions);
        const bx = Array.isArray(sh.boxes) ? sh.boxes : [];
        const n = sh.box_count || bx.length || 1;
        if (n > 1 && bx.length) {
          setBoxMode("multi"); setSameSize(false);
          setBoxes(bx.map((x, i) => ({ id: i + 1, qty: 1, l: String(x.l || ""), b: String(x.b || ""), h: String(x.h || ""), divisor: x.divisor || 4000, deadWt: x.deadWt ? String(x.deadWt) : "" })));
        } else {
          setBoxMode("single");
          const b0 = bx[0] || {};
          if (b0.deadWt || sh.package?.weight) setDeadWeight(String(b0.deadWt || sh.package.weight));
          if (b0.l) setLength(String(b0.l)); if (b0.b) setBreadth(String(b0.b)); if (b0.h) setHeight(String(b0.h));
        }
        if (sh.warehouse) setCloneWh(sh.warehouse);
        if (sh.delivery_location_id) setDeliveryLocation(sh.delivery_location_id);
        toast.success(`Cloned from order ${sh.order_id}. Review and edit, then book - a new order ID is created.` + (cust ? "" : " Please select the customer."), { duration: 9000 });
      } catch (e) {
        toast.error(e?.response?.data?.detail || "Could not load the shipment to clone");
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  const handleSubmit = async () => {
    if (isPartner) {
      if (!partnerCustomerName.trim()) { toast.error("Please enter a customer name."); return; }
    } else if (!selectedCustomer) { toast.error("Please select a customer first."); return; }
    if (!senderName || !receiverName) { toast.error("Sender and receiver names are required."); return; }
    if (!senderPhone || senderPhone.length !== 10) {
      toast.error("Sender phone must be 10 digits");
      return;
    }
    if (!receiverPhone || receiverPhone.length !== 10) {
      toast.error("Receiver phone must be 10 digits");
      return;
    }
    if (!senderAddress || !senderAddress.trim()) {
      toast.error("Sender address is required");
      return;
    }
    if ((parseFloat(finalChargeableWt) >= 20 || parseFloat(consignmentValue) > 50000) && !invoiceFile && invoiceMode !== "generate") {
         toast.error("Invoice or Delivery Challan is mandatory for shipments ≥20kg or above ₹50,000 in value");
         return;
       }
    if ((parseFloat(finalChargeableWt) >= 20 || parseFloat(consignmentValue) > 50000) && invoiceMode !== "generate" && !invoiceNumber.trim()) {
         toast.error("Invoice number is mandatory - enter the number printed on the customer's invoice");
         return;
       }
    if (!receiverAddress || !receiverAddress.trim()) {
      toast.error("Receiver address is required");
      return;
    }if (!/\d/.test(receiverAddress) || receiverAddress.trim().length < 15) {
         toast.error("Receiver address looks incomplete — please include a door/flat number, street, and landmark (e.g. \"D.No 12-34, Main Road, Opp XYZ, Akkayyapalem\")");
         return;
       }
    if (pickupMode === "schedule" && !pickupConfirmed) {
      toast.error("Please confirm pickup date and slot first");
      return;
    }
    if (!fromCity || !toCity) { toast.error("Origin and destination cities are required."); return; }
    const addrBlocks = addrIssues.filter((i) => i.level === "block");
    if (addrBlocks.length && !window.confirm(`ADDRESS PROBLEM - this booking may need to be cancelled later:\n\n${addrBlocks.map((i) => i.message).join("\n\n")}\n\nBook anyway?`)) return;
    if (!fromPincode || !isValidPincode(fromPincode)) {
      setFromPincodeError("Valid 6-digit pincode is required");
      toast.error("Origin pincode must be a valid 6-digit number."); return;
    }
    if (!toPincode || !isValidPincode(toPincode)) {
      setToPincodeError("Valid 6-digit pincode is required");
      toast.error("Destination pincode must be a valid 6-digit number."); return;
    }
    if (requiresEwayBill && !ewayBill.trim()) {
      toast.error("Eway Bill number is required for shipments above ₹50,000"); return;
    }
    if (pickupMode === "schedule" && !warehouse) {
      toast.error("Please select a pickup location."); return;
    }
    // Real incident (Aug 21 2026, order 21082679): origin pincode/city and
    // the selected FM Pickup warehouse silently drifted apart, producing a
    // shipment with internally contradictory origin data that confused
    // Delhivery's manifest routing. Upgraded (Aug 22 2026, per Aditya)
    // from the original state-level check to an EXACT pincode match --
    // Delhivery routes pickups to specific facilities by exact pincode,
    // not by city or state, so even same-city pincode differences can hit
    // a different facility catchment. No tolerance, even within one city.
    if (pickupMode === "schedule" && warehouse) {
      const selectedWHCheck = warehouseList.find(w => w.id === warehouse);
      if (selectedWHCheck && fromPincode && isValidPincode(fromPincode)) {
        if (selectedWHCheck.pincode?.trim() !== fromPincode.trim()) {
          toast.error(
            `Pickup location mismatch: the selected warehouse's pincode is ` +
            `${selectedWHCheck.pincode}, but the origin pincode is ${fromPincode}. ` +
            `Delhivery routes pickups by exact pincode -- please select a warehouse ` +
            `with pincode ${fromPincode}, or correct the origin pincode.`
          );
          return;
        }
      }
    }

    setLoading(true);
    try {
      const groupId = bookingGroupId || `BG-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
      setBookingGroupId(groupId);
      const finalPrice = billOverride ? parseFloat(billOverride) : estimatedPrice;
      const selectedWH = warehouseList.find(w => w.id === warehouse);

      const payload = {
        customer_id: isPartner ? null : selectedCustomer.id,
        customer_name: isPartner ? partnerCustomerName.trim() : (selectedCustomer?.name || null),
        booking_group_id: groupId,
        account_type: accountType,
        pickup_mode: pickupMode,
        warehouse: selectedWH?.code || warehouse,
        warehouse_address: selectedWH ? `${selectedWH.name}, ${selectedWH.address}, ${selectedWH.city} - ${selectedWH.pincode}` : "",
        shipping_mode: mode,
        payment_mode: paymentMode,
        consignment_type: consignmentType,
        material_description: materialDesc,
        consignment_value: consignmentValue ? parseFloat(consignmentValue) : null,
        cod_amount: paymentMode === "cod" && codAmount ? parseFloat(codAmount) : null,
        product_category: productCategory || null,
        connection_through: connectionThrough || null,
        service_level: mode === "surface" ? "STANDARD" : mode === "express" ? "EXPRESS" : "NDD",
        insurance_type: insuranceType,
        insurance_premium: insurancePremium,
        eway_bill_number: ewayBill || null,
        volumetric_weight: boxMode === "single" ? singleVolWt : parseFloat(multiTotalVolWt.toFixed(2)),
        chargeable_weight: parseFloat(finalChargeableWt.toFixed(2)),
        box_count: totalAwbCount,
        same_size_boxes: boxMode === "multi" && sameSize ? {
          count: totalBoxCount,
          length_cm: parseFloat(sameSizeData.l) || 0,
          breadth_cm: parseFloat(sameSizeData.b) || 0,
          height_cm: parseFloat(sameSizeData.h) || 0,
          dead_weight_per_box: parseFloat(sameSizeData.deadWt) || 0,
          divisor: sameSizeData.divisor
        } : null,
        bill_amount_override: billOverride ? parseFloat(billOverride) : null,
        special_instructions: specialInstructions,
        seller_details: showSeller ? { name: sellerName, phone: sellerPhone, gst: sellerGST, address: sellerAddress } : null,
        boxes: boxMode === "multi"
          ? (sameSize
              ? Array.from({ length: totalBoxCount }, (_, i) => ({
                  id: i + 1,
                  qty: 1,
                  l: sameSizeData.l,
                  b: sameSizeData.b,
                  h: sameSizeData.h,
                  divisor: sameSizeData.divisor,
                  deadWt: sameSizeData.deadWt,
                }))
              : boxes.flatMap((b) =>
                  Array.from({ length: parseInt(b.qty) || 1 }, () => ({ ...b, qty: 1 }))
                ))
          : undefined,
        pickup_date_offset: pickupMode === "schedule" ? pickupDate : null,
        pickup_slot: pickupMode === "schedule" ? pickupSlot : null,
        pickup_contact_name: pickupContact || null,
        pickup_contact_phone: pickupPhone || null,
        // Pickup address = the PICKUP LOCATION's address in schedule mode (where the carrier really collects the goods),
        // NOT the customer's billing/sender address. The sender address stays on the booking as sender_address for documents.
        origin: {
          name: senderName, phone: senderPhone,
          address: (pickupMode === "schedule" && selectedWH?.address) ? selectedWH.address : senderAddress,
          city: fromCity, pincode: fromPincode,
          sender_address: senderAddress,
        },
        destination: { name: receiverName, phone: receiverPhone, address: receiverAddress, city: toCity, pincode: toPincode },
        package: { weight: finalChargeableWt, type: consignmentType, description: materialDesc },
        // Real gap fix (Sep 18 2026): warehouse/delivery_location_id were
        // never sent here before, even though the backend's ShipmentCreate
        // model expects warehouse -- meant every shipment's warehouse field
        // silently stayed blank regardless of which pickup location was
        // selected in the dropdown above, breaking the downstream B2B
        // manifest pickup-location lookup and the PUR auto-attach hook.
        warehouse: warehouse || null,
        warehouse_address: selectedWH ? [selectedWH.address, selectedWH.city].filter(Boolean).join(", ") : null,
        delivery_location_id: deliveryLocation || null,
      };

      const res = await api.post("/shipments", payload);
      if (offlineDraft) { // additive: close the Excel draft once the real booking succeeded
        try { await api.patch(`/v2/offline-bookings/drafts/${offlineDraft.id}`, { status: "BOOKED", shipment_id: res.data?.id }); setOfflineDraft(null); } catch { }
      }
      if (pickupMode === "schedule") {
        try {
          const pickupDateStr = new Date(Date.now() + pickupDate * 24 * 60 * 60 * 1000)
            .toISOString().slice(0, 10);
          // Remember the slot the customer asked for, so the Delhivery pickup can be raised for it later.
          api.post(`/v2/manifest-gate/pickup-desk/${res.data.id}/requested-slot`, { pickup_date_offset: Number(pickupDate) || 0, pickup_slot: pickupSlot }).catch(() => {});
          const purRes = await api.post("/v2/pickup-requests", {
            customer_id: selectedCustomer?.id || null,
            pickup_location_id: warehouse,
            pickup_date: pickupDateStr,
            pickup_slot: pickupSlot,
            contact_person: pickupContact || senderName,
            contact_phone: pickupPhone || senderPhone,
            box_count: totalAwbCount,
            shipment_ids: [res.data.id],
          });
          if (purRes.data.auto_consolidated) {
            toast.success(`Added to existing pickup request ${purRes.data.pickup_id} for today`);
          } else {
            toast.success(`Pickup request ${purRes.data.pickup_id} created`);
          }
        } catch (purErr) {
          toast.error(purErr.response?.data?.detail || "Failed to raise pickup request — please create one manually in Pickup Console");
        }
      }
      if (photos.length > 0) {
        let uploadedCount = 0;
        for (const p of photos) {
          if (!p.file) continue;
          try {
            const fd = new FormData();
            fd.append("file", p.file);
            await api.post(`/shipments/${res.data.id}/photos`, fd, {
              headers: { "Content-Type": "multipart/form-data" },
            });
            uploadedCount++;
          } catch (photoErr) {
            toast.error(`Failed to upload photo: ${p.name}`);
          }
        }
        if (uploadedCount > 0) {
          toast.success(`${uploadedCount} photo${uploadedCount > 1 ? "s" : ""} uploaded`);
        }
      }
      if (invoiceNumber.trim() || ewayBill.trim()) {
        try {
          await api.put(`/v2/shipment-docs/${res.data.id}/booking-docs`, { invoice_number: invoiceNumber.trim() || undefined, eway_bill_number: ewayBill.trim() || undefined });
        } catch { toast.error("Invoice / e-way bill number could not be saved - add them from Edit shipment details"); }
      }
      if (ewayFile) {
        try {
          const fd = new FormData(); fd.append("file", ewayFile);
          await api.post(`/v2/shipment-docs/${res.data.id}/eway-bill`, fd, { headers: { "Content-Type": "multipart/form-data" } });
          toast.success("E-way bill uploaded");
        } catch { toast.error("E-way bill upload failed - upload it again from All Shipments"); }
      }
      if ((parseFloat(finalChargeableWt) >= 20 || parseFloat(consignmentValue) > 50000)) {
         if (invoiceFile) {
           try {
             const fd = new FormData();
             fd.append("file", invoiceFile);
             await api.post(`/shipments/${res.data.id}/invoice`, fd, {
               headers: { "Content-Type": "multipart/form-data" },
             });
             toast.success("Invoice uploaded");
           } catch (invErr) {
             toast.error("Invoice upload failed — please upload manually from All Shipments");
           }
         }
         // Note: "Generate Delivery Challan" is intentionally NOT called
         // here anymore. The backend requires payment_status="paid" before
         // generating, but nothing has marked this shipment paid yet at
         // this exact point in the booking flow (cash collection, wallet
         // gate at connect, and UPI webhook all happen after or separately
         // from this step) -- so this call could never actually succeed,
         // and always surfaced a scary but meaningless error. The invoice
         // now auto-generates itself, correctly, the moment real payment
         // is confirmed (mark_cash_paid, the Cashfree webhook, and the
         // wallet/credit gate at connect-time all trigger it automatically).
       }
      if (paymentMode === "cash") {
        try {
          await api.post(`/v2/payments/shipments/${res.data.id}/mark-cash-paid`);
          toast.success(`Order booked & cash collected! AWB: ${res.data.order_id} (${totalAwbCount} label${totalAwbCount > 1 ? "s" : ""})`);
        } catch (cashErr) {
          toast.error("Order booked, but marking cash payment failed — please do it manually from All Shipments");
        }
        finishBooking(res.data.order_id, totalAwbCount);
      } else if (paymentMode === "ppp_cash") {
        // Pay Post Pickup, intended cash — book normally, no collection now.
        // payment_status stays pending; staff collects later via the
        // existing "Mark Paid (Cash)" action on All Shipments, whenever
        // payment actually comes in (before or after connecting).
        toast.success(`Order booked! Pay Post Pickup (Cash) — AWB: ${res.data.order_id} (${totalAwbCount} label${totalAwbCount > 1 ? "s" : ""})`);
        finishBooking(res.data.order_id, totalAwbCount);
      } else {
        // ppp_upi always generates a link immediately (to send whenever),
        // same as an underfunded wallet's "prepaid" — but ppp_upi does NOT
        // block connecting even if unpaid (bypasses the wallet gate, unlike
        // plain "prepaid").
        const needsPayment = (paymentMode === "prepaid" && customerWallet && customerWallet.balance < res.data.price) || paymentMode === "ppp_upi";
        if (needsPayment) {
          try {
            const linkRes = await api.post(`/v2/payments/shipments/${res.data.id}/payment-link`);
            setQrModal({ url: linkRes.data.link_url, orderId: res.data.order_id, linkId: linkRes.data.link_id, awbCount: totalAwbCount });
            toast.success(`Order booked! AWB: ${res.data.order_id} (${totalAwbCount} label${totalAwbCount > 1 ? "s" : ""})`);
          } catch (qrErr) {
            toast.error("Could not generate payment link — you can send one later from All Shipments");
            toast.success(`Order booked! AWB: ${res.data.order_id} (${totalAwbCount} label${totalAwbCount > 1 ? "s" : ""})`);
            finishBooking(res.data.order_id, totalAwbCount);
          }
        } else {
          toast.success(`Order booked! AWB: ${res.data.order_id} (${totalAwbCount} label${totalAwbCount > 1 ? "s" : ""})`);
          finishBooking(res.data.order_id, totalAwbCount);
        }
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Booking failed. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const selectedWH = React.useMemo(() => 
    warehouseList.find(w => w.id === warehouse),
    [warehouseList, warehouse]
  );


  // Gate EXECUTION partners (AFTER all hooks to respect rules-of-hooks) --
  // mirrors PartnerBookings.jsx's exact existing behavior.
  if (isPartner && partnerType === "EXECUTION") {
    return (
      <DashboardLayout portalColor="green">
        <div className="flex flex-col items-center justify-center h-full text-center py-16" data-testid="access-denied">
          <AlertTriangle className="w-16 h-16 text-red-400 mb-4" />
          <h2 className="text-xl font-bold text-slate-800 mb-2">Access Denied</h2>
          <p className="text-sm text-slate-500 max-w-md">
            EXECUTION partners cannot create bookings. This feature is only available to
            NETWORK and REVENUE partners.
          </p>
        </div>
      </DashboardLayout>
    );
  }
  return (
    <DashboardLayout>
      <div className="max-w-5xl space-y-5 pb-10">

        {/* ── HEADER: Order ID ── */}
        <div className="flex items-center justify-between bg-white rounded-lg border border-slate-200 p-4">
          <div>
            <h1 className="font-heading text-2xl font-bold text-slate-900 uppercase">New Booking Console</h1>
            <p className="text-slate-500 text-sm mt-0.5">Create a new shipment order with all details.</p>
          </div>
          <div className="bg-brand-50 px-5 py-3 rounded-lg border border-brand-200 text-center">
            <p className="text-xs text-slate-500 font-semibold uppercase">Order ID</p>
            <p className="font-heading font-bold text-2xl text-brand-600" data-testid="order-id-display">
              {fetchingOrderId ? "..." : orderId}
            </p>
            
          </div>
        </div>

        {/* ── ROW 1: Account Type + Pickup Mode (two separate cards side by side) ── */}
        <div className="grid grid-cols-2 gap-5">
          {/* Account Type -- now Walk-in / Wallet / Credit, replacing the old Domestic/International/Other */}
          <div className="bg-white rounded-lg border border-slate-200 p-5">
            <h2 className="font-heading font-semibold text-xs uppercase tracking-wider text-slate-500 mb-3">Account Type</h2>
            <div className="flex gap-2 flex-wrap">
              {[
                { key: "walkin", label: "Walk-in", icon: "\u{1F464}" },
                { key: "wallet", label: "Wallet", icon: "\u{1F4B0}" },
                { key: "credit", label: "Credit", icon: "\u{1F9FE}" },
              ].map(({ key, label, icon }) => (
                <button key={key} data-testid={`account-type-${key}`}
                  onClick={() => {
                    setAccountType(key);
                    setShowWalkinCustomer(key === "walkin");
                    if (key === "walkin") setShowAddCustomer(false);
                  }}
                  className={`px-5 py-2 rounded-sm text-sm font-semibold uppercase tracking-wide transition-all ${accountType === key ? "bg-brand-500 text-white shadow-sm" : "bg-slate-100 text-slate-600 hover:bg-slate-200"}`}>
                  {icon} {label}
                </button>
              ))}
            </div>
          </div>

          {/* Pickup Mode */}
          <div className="bg-white rounded-lg border border-slate-200 p-5">
            <h2 className="font-heading font-semibold text-xs uppercase tracking-wider text-slate-500 mb-3">Pickup Mode</h2>
            <div className="flex rounded-sm border border-slate-200 overflow-hidden w-fit">
              {[["schedule", "FM Pickup"], ["counter", "Self Drop"]].map(([val, lbl]) => (
                <button key={val} onClick={() => { setPickupMode(val); if (val === "counter") setPickupConfirmed(false); }}
                  className={`px-5 py-2 text-sm font-semibold transition-colors ${pickupMode === val ? "bg-slate-800 text-white" : "bg-white text-slate-600 hover:bg-slate-50"}`}>
                  {lbl}
                </button>
              ))}
            </div>
            {pickupMode === "counter" && (
              <p className="text-xs text-slate-400 mt-2">
                Customer drops shipment at nearest Sendtoo point. Can be converted to scheduled pickup before first FM scan.
              </p>
            )}
          </div>
        </div>

        {/* ── ROW 2: Customer (left) + Pickup Location (right) ── */}
        <div className="bg-white rounded-lg border border-slate-200 p-5">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">

            {/* LEFT: Customer */}
            <div>
              <div className="flex items-center justify-between mb-3">
                <h2 className="font-heading font-semibold text-xs uppercase tracking-wider text-slate-500">
                  Customer (Billing Account)<span className="text-red-500 ml-0.5">*</span>
                </h2>
                <div className="flex items-center gap-3">
                  {accountType !== "walkin" && (
                    <button onClick={() => { setShowAddCustomer(!showAddCustomer); setShowWalkinCustomer(false); }}
                      className="flex items-center gap-1 text-xs text-brand-500 hover:text-brand-600 font-semibold">
                      <Plus className="w-3.5 h-3.5" />
                      {showAddCustomer ? "Cancel" : "Add New"}
                    </button>
                  )}
                </div>
              </div>

              {/* Inline Add Customer Form */}
              {showAddCustomer && (
                <div className="bg-slate-50 border border-slate-200 rounded-lg p-3 mb-3">
                  <h3 className="font-semibold text-xs text-slate-700 mb-2">Add New Customer</h3>
                  <DuplicateWarning dup={dupNew} onUse={selectExistingCustomer} hardMessage="This customer already exists and cannot be created again." hint="Use the existing customer below, or ask an admin to merge duplicates." />
                  <div className="grid grid-cols-2 gap-2">
                    <Field label="Full Name" required>
                      <input value={newCustomer.name} onChange={(e) => setNewCustomer({ ...newCustomer, name: e.target.value })}
                        placeholder="Customer name" className={dupNew.fieldMsg.name ? dupCls : inputCls} data-testid="new-customer-name" />
                    </Field>
                    <Field label="Email" required>
                      <input type="email" value={newCustomer.email} onChange={(e) => setNewCustomer({ ...newCustomer, email: e.target.value })}
                        placeholder="email@example.com" className={dupNew.fieldMsg.email ? dupCls : inputCls} data-testid="new-customer-email" />
                    </Field>
                    <Field label="Phone" required>
                      <input value={newCustomer.phone} onChange={(e) => setNewCustomer({ ...newCustomer, phone: e.target.value })}
                        placeholder="+91 9876543210" className={dupNew.fieldMsg.mobile ? dupCls : inputCls} data-testid="new-customer-phone" />
                    </Field>
                    <Field label="Password">
                      <input value={newCustomer.password} onChange={(e) => setNewCustomer({ ...newCustomer, password: e.target.value })}
                        placeholder="Customer@123" className={inputCls} />
                    </Field>
                  </div>
                  <button onClick={handleAddCustomer} disabled={savingCustomer || dupNew.blocked}
                    className="mt-2 flex items-center gap-2 px-3 py-1.5 bg-brand-500 text-white text-xs font-semibold rounded-sm hover:bg-brand-600 disabled:opacity-50"
                    data-testid="save-customer-btn" title={dupNew.blocked ? "Resolve the duplicate warning first" : undefined}>
                    {savingCustomer ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Plus className="w-3.5 h-3.5" />}
                    Save Customer
                  </button>
                </div>
              )}
              {/* Inline Retail Walk-in Form */}
              {showWalkinCustomer && (
                <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3 mb-3">
                  <h3 className="font-semibold text-xs text-slate-700 mb-2">Retail Walk-in Customer</h3>
                  <DuplicateWarning dup={dupWalkin} onUse={selectExistingCustomer} hardMessage="A customer with this mobile already exists. Save & Select will use that customer." hint="Click Use this customer, or Save & Select \u2014 no new account is created." />
                  <div className="grid grid-cols-1 gap-2">
                    <Field label="Full Name" required>
                      <input value={walkinCustomer.name} onChange={(e) => setWalkinCustomer({ ...walkinCustomer, name: e.target.value })}
                        placeholder="Customer name" className={dupWalkin.fieldMsg.name ? dupCls : inputCls} data-testid="walkin-customer-name" />
                    </Field>
                    <Field label="Mobile" required>
                      <input value={walkinCustomer.mobile} onChange={(e) => setWalkinCustomer({ ...walkinCustomer, mobile: e.target.value })}
                        placeholder="9876543210" className={dupWalkin.fieldMsg.mobile ? dupCls : inputCls} data-testid="walkin-customer-mobile" />
                    </Field>
                    <Field label="Address" required>
                      <input value={walkinCustomer.address} onChange={(e) => setWalkinCustomer({ ...walkinCustomer, address: e.target.value })}
                        placeholder="Full address" className={inputCls} data-testid="walkin-customer-address" />
                    </Field>
                    <Field label="Pincode" required>
                      <input value={walkinCustomer.pincode}
                        onChange={(e) => setWalkinCustomer({ ...walkinCustomer, pincode: e.target.value.replace(/\D/g, "").slice(0, 6) })}
                        placeholder="530016" maxLength={6} className={inputCls} data-testid="walkin-customer-pincode" />
                    </Field>
                  </div>
                  <button onClick={handleAddWalkinCustomer} disabled={savingWalkin || (!dupWalkin.hard && dupWalkin.blocked)}
                    className="mt-2 flex items-center gap-2 px-3 py-1.5 bg-emerald-600 text-white text-xs font-semibold rounded-sm hover:bg-emerald-700 disabled:opacity-50"
                    data-testid="save-walkin-btn">
                    {savingWalkin ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Plus className="w-3.5 h-3.5" />}
                    Save & Select
                  </button>
                </div>
              )}

              {/* Customer Search */}
              {isPartner ? (
                <div className="relative">
                  <input value={partnerCustomerName}
                    onChange={(e) => setPartnerCustomerName(e.target.value)}
                    placeholder="Enter customer name"
                    className={inputCls} data-testid="partner-customer-name" />
                </div>
              ) : (
              <div className="relative">
                <input value={customerSearch}
                  onChange={(e) => { setCustomerSearch(e.target.value); setShowCustomerDropdown(true); }}
                  onFocus={() => setShowCustomerDropdown(true)}
                  placeholder="Search by name, phone, or email..."
                  className={inputCls} data-testid="booking-customer-search" />

                {selectedCustomer && (
                  <div className="mt-2 bg-brand-50 border border-brand-100 rounded-sm p-2 flex items-center justify-between">
                    <div>
                      <p className="text-sm font-semibold text-brand-700">{selectedCustomer.name}{selectedCustomer.customer_code && <span className="ml-2 font-mono text-xs text-slate-500">#{selectedCustomer.customer_code}</span>}</p>
                      <p className="text-xs text-slate-400">{selectedCustomer.email}</p>
                      {selectedCustomer.manual_rate_card && (
                        <p className="text-xs font-semibold mt-1 text-amber-700" data-testid="manual-rate-card-hint">
                          Manual rate card customer - no agreement on file. Enter the agreed price in "Bill amount override" before booking.
                        </p>
                      )}
                      {customerWallet && selectedCustomer.customer_segment !== "WALKIN" && !selectedCustomer.email?.endsWith("@walkin.sendtoo.in") && (
                        <p className={`text-xs font-semibold mt-1 ${
                          customerWallet.balance < 100 ? "text-red-600"
                          : customerWallet.balance < 500 ? "text-orange-500"
                          : "text-green-700"
                        }`}>
                          Wallet: ₹{customerWallet.balance.toFixed(2)}
                          {customerWallet.credit_enabled && ` (Credit limit: ₹${customerWallet.credit_limit.toFixed(2)})`}
                        </p>
                      )}
                    </div>
                    <button onClick={() => { setSelectedCustomer(null); setCustomerWallet(null); setWarehouse(""); setCustomerSearch(""); setShowAddLocation(false); fetchWarehouses(); }}
                      className="text-slate-400 hover:text-red-500"><X className="w-4 h-4" /></button>
                  </div>
                )}

                {showCustomerDropdown && !selectedCustomer && filteredCustomers.length > 0 && (
                  <div className="absolute z-20 top-full left-0 right-0 bg-white border border-slate-200 rounded-sm shadow-lg mt-1 max-h-48 overflow-y-auto">
                    {filteredCustomers.map(c => (
                      <button key={c.id} onClick={() => { setSelectedCustomer(c); fetchCustomerPickupLocations(c.id); fetchCustomerDeliveryLocations(c.id); fetchCustomerDefaultLocation(c.id); fetchCustomerWallet(c.id); setCustomerSearch(""); setShowCustomerDropdown(false); setShowAddLocation(false); }}
                        className="w-full flex items-center gap-3 px-3 py-2 hover:bg-brand-50 text-left">
                        <div className="w-7 h-7 rounded-full bg-brand-500 flex items-center justify-center text-white text-xs font-bold">{c.name?.[0]}</div>
                        <div>
                          <p className="text-sm font-semibold text-slate-800">{c.name}</p>
                          <p className="text-xs text-slate-400">{c.customer_code && `#${c.customer_code} • `}{c.email}{c.phone && ` • ${c.phone}`}</p>
                        </div>
                      </button>
                    ))}
                  </div>
                )}

                {showCustomerDropdown && !selectedCustomer && filteredCustomers.length === 0 && customerSearch && (
                  <div className="absolute z-20 top-full left-0 right-0 bg-white border border-slate-200 rounded-sm shadow-lg mt-1 p-4 text-center">
                    <p className="text-sm text-slate-500">No customer found</p>
                    <button onClick={() => setShowAddCustomer(true)} className="text-sm text-brand-500 hover:underline mt-1">+ Add New Customer</button>
                  </div>
                )}
              </div>
              )}
            </div>

            {/* RIGHT: Pickup Location */}
            <div>
              <div className="flex items-center justify-between mb-3">
                <h2 className="font-heading font-semibold text-xs uppercase tracking-wider text-slate-500">
                  Pickup Location / Warehouse
                  {pickupMode === "schedule" && <span className="text-red-500 ml-0.5">*</span>}
                </h2>
                {pickupMode === "schedule" && selectedCustomer && (
                  <button onClick={() => setShowAddLocation(!showAddLocation)}
                    className="flex items-center gap-1 text-xs text-brand-500 hover:text-brand-600 font-semibold">
                    <Plus className="w-3.5 h-3.5" />
                    {showAddLocation ? "Cancel" : "Add New"}
                  </button>
                )}
              </div>

              {/* Location picker — shown for both Schedule Pickup and Self Drop */}
              {!selectedCustomer ? (
                /* No customer selected yet */
                <div className="flex items-center justify-center h-28 bg-slate-50 border-2 border-dashed border-slate-200 rounded-lg">
                  <p className="text-sm text-slate-400 text-center px-6">Select a customer first to load pickup locations</p>
                </div>
              ) : (
                /* Customer selected */
                <div className="space-y-4">
                  {pickupMode === "counter" && (
                    <div className="bg-slate-50 border border-dashed border-slate-200 rounded-lg p-3">
                      <p className="text-xs text-slate-400">
                        Self Drop — no pickup scheduling needed. Address below is used for Sender/Vendor details.
                      </p>
                    </div>
                  )}
                  <select value={warehouse}
                    onChange={(e) => {
                      const sel = warehouseList.find(w => w.id === e.target.value);
                      const isLinked = !sel?.customer_id || sel?.customer_id === selectedCustomer?.id;
                      if (e.target.value && !isLinked) {
                        setPendingWarehouse(e.target.value);
                        setShowLocationWarning(true);
                      } else {
                        setWarehouse(e.target.value);
                        setPickupConfirmed(false);
                        setPickupContact(sel?.contact_person || "");
                        setPickupPhone(sel?.contact_phone || "");
                        // Auto-fill origin pincode/city from the selected
                        // warehouse's known-correct values -- prevents the
                        // class of typo where a manually-typed pincode
                        // doesn't match the actual pickup location (e.g. a
                        // Mumbai pincode entered for a Vizag warehouse).
                        // Staff can still edit the fields afterward if needed.
                        if (sel?.pincode) { setFromPincode(sel.pincode); setFromPincodeError(""); }
                        if (sel?.city) { setFromCity(sel.city); }
                      }
                    }}
                    className={selectCls} data-testid="warehouse-select">
                    <option value="">— Select Pickup Location —</option>
                    {warehouseList.map(w => (
                      <option key={w.id} value={w.id}>{w.code} - {w.name} ({w.city})</option>
                    ))}
                  </select>

                  {showAddLocation && selectedCustomer && (
                    <div className="bg-slate-50 border border-slate-200 rounded-lg p-3 mt-2" data-testid="add-location-form">
                      <h3 className="text-xs font-bold text-slate-700 mb-2">
                        Add Location for {selectedCustomer.name}
                      </h3>
                      <div className="grid grid-cols-2 gap-2">
                        <Field label="Location Name" required>
                          <input value={newLocation.name}
                            onChange={(e) => setNewLocation({ ...newLocation, name: e.target.value })}
                            placeholder="e.g. Main Office"
                            className={inputCls}
                            data-testid="new-location-name" />
                        </Field>
                        <Field label="Pincode" required>
                          <input value={newLocation.pincode}
                            onChange={(e) => setNewLocation({ ...newLocation, pincode: e.target.value.replace(/\D/g, "").slice(0, 6) })}
                            placeholder="530001"
                            maxLength={6}
                            className={inputCls}
                            data-testid="new-location-pincode" />
                        </Field>
                        <Field label="City" required>
                          <input value={newLocation.city}
                            onChange={(e) => setNewLocation({ ...newLocation, city: e.target.value })}
                            placeholder="Visakhapatnam"
                            className={inputCls}
                            data-testid="new-location-city" />
                        </Field>
                        <Field label="Contact Phone">
                          <input value={newLocation.contact_phone}
                            onChange={(e) => setNewLocation({ ...newLocation, contact_phone: cleanPhone(e.target.value) })}
                            placeholder="9876543210"
                            maxLength={10}
                            className={inputCls}
                            data-testid="new-location-phone" />
                        </Field>
                        <div className="col-span-2">
                          <Field label="Full Address" required>
                            <input value={newLocation.address}
                              onChange={(e) => setNewLocation({ ...newLocation, address: e.target.value })}
                              placeholder="Street, landmark..."
                              className={inputCls}
                              data-testid="new-location-address" />
                          </Field>
                        </div>
                        <div className="col-span-2">
                          <Field label="Contact Person">
                            <input value={newLocation.contact_person}
                              onChange={(e) => setNewLocation({ ...newLocation, contact_person: e.target.value })}
                              placeholder="Person name"
                              className={inputCls}
                              data-testid="new-location-contact-person" />
                          </Field>
                        </div>
                      </div>
                      <button
                        onClick={async () => {
                          if (!newLocation.name || !newLocation.address || !newLocation.city || !newLocation.pincode) {
                            toast.error("Name, address, city and pincode are required");
                            return;
                          }
                          setSavingLocation(true);
                          try {
                            await api.post(
                              `/v2/pickup-locations/create`,
                              {
                                owner_id: selectedCustomer.id,
                                owner_type: "CUSTOMER",
                                location_name: newLocation.name,
                                contact_person: newLocation.contact_person,
                                contact_mobile: newLocation.contact_phone,
                                address_line1: newLocation.address,
                                address_line2: "",
                                city: newLocation.city,
                                pincode: newLocation.pincode,
                                state: "",
                                location_type: "WAREHOUSE",
                                // Only enforced when staff has already started
                                // entering this booking's real origin pincode --
                                // ensures a location added here genuinely
                                // represents this shipment's actual origin
                                // (Step 2, Aug 22 2026).
                                ...(fromPincode ? { origin_pincode_context: fromPincode } : {}),
                              }
                            );
                            setShowAddLocation(false);
                            fetchCustomerPickupLocations(selectedCustomer.id);
                          } catch (err) {
                            toast.error(err.response?.data?.detail || "Failed to save location");
                          } finally {
                            setSavingLocation(false);
                          }
                        }}
                        disabled={savingLocation}
                        data-testid="save-new-location-btn"
                        className="mt-2 flex items-center gap-2 px-3 py-1.5 bg-brand-500 text-white text-xs font-semibold rounded-sm hover:bg-brand-600 disabled:opacity-50">
                        {savingLocation
                          ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                          : <Plus className="w-3.5 h-3.5" />}
                        Save Location
                      </button>
                    </div>
                  )}

                  {/* Address preview */}
                  {selectedWH && (
                    <div className="bg-slate-50 border border-slate-200 rounded-lg p-3 text-xs">
                      <div className="flex items-center justify-between">
                        <p className="font-semibold text-slate-700">{selectedWH.name}</p>
                        {pickupConfirmed && pickupMode === "schedule" && (
                          <div className="flex items-center gap-2">
                            <span className="text-green-700 font-semibold text-[10px]">
                              Pickup Confirmed · {pickupDate === 0 ? "Today" : pickupDate === 1 ? "Tomorrow" : (() => {
                                const d = new Date();
                                d.setDate(d.getDate() + pickupDate);
                                return d.toLocaleDateString("en-IN", { day: "numeric", month: "short" });
                              })()}
                            </span>
                            <button onClick={() => setPickupConfirmed(false)} data-testid="edit-pickup-btn-inline"
                              className="text-[10px] text-brand-500 hover:underline font-semibold">
                              Edit
                            </button>
                          </div>
                        )}
                      </div>
                      <p className="text-slate-400 mt-0.5">{selectedWH.address}</p>
                      <p className="text-slate-400">{selectedWH.city} — {selectedWH.pincode}</p>
                        </div>
                      )}
                  {/* 7-day date pills — only after location selected */}
                  {(warehouse || selectedWH) && pickupMode === "schedule" && (
                    <>
                      {!pickupConfirmed && (
                        <>
                          <div>
                            <label className="block text-xs font-semibold text-slate-500 mb-2">Pickup Date</label>
                            <div className="flex gap-1.5 flex-wrap">
                              {Array.from({ length: 7 }, (_, i) => {
                                const d = new Date();
                                d.setDate(d.getDate() + i);
                                return (
                                  <button key={i} onClick={() => setPickupDate(i)}
                                    className={`flex flex-col items-center px-2.5 py-1.5 rounded-full border-2 min-w-[44px] transition-all text-xs ${pickupDate === i ? "bg-slate-800 border-slate-800 text-white" : "bg-white border-slate-200 text-slate-600 hover:border-slate-400"}`}>
                                    <span className="font-semibold">{d.toLocaleDateString("en-IN", { weekday: "short" })}</span>
                                    <span className="font-bold text-sm">{d.getDate()}</span>
                                    <span>{d.toLocaleDateString("en-IN", { month: "short" })}</span>
                                  </button>
                                );
                              })}
                            </div>
                            {mode === "ndd" && (
                              <p className="text-xs text-red-500 mt-1.5 font-semibold">NDD: ensure pickup before 6:00 PM</p>
                            )}
                          </div>

                          {/* Slot picker */}
                          <div>
                            <label className="block text-xs font-semibold text-slate-500 mb-2">Pickup Slot</label>
                            <div className="grid grid-cols-3 gap-2">
                              {[
                                ["MIDDAY", "☀️", "Mid Day", "10:00-14:00"],
                                ["EVENING", "🌙", "Evening", "14:00-18:00"],
                                ["LATE_EVENING", "🌃", "Late Evening", "18:00-21:00"]
                              ].map(([val, icon, lbl, time]) => (
                                <button key={val} onClick={() => setPickupSlot(val)}
                                  className={`flex flex-col items-center py-2 px-1 rounded-lg border-2 transition-all text-xs ${pickupSlot === val ? "border-brand-500 bg-brand-50 text-brand-700" : "border-slate-200 bg-white text-slate-600 hover:border-slate-300"}`}>
                                  <span className="text-base">{icon}</span>
                                  <span className="font-semibold">{lbl}</span>
                                  <span className="text-slate-400">{time}</span>
                                </button>
                              ))}
                            </div>
                          </div>

                          {/* Confirm button */}
                          <button
                            onClick={() => setPickupConfirmed(true)}
                            data-testid="confirm-pickup-btn"
                            className="mt-2 px-4 py-1.5 bg-brand-500 text-white text-xs font-semibold rounded-sm hover:bg-brand-600">
                            Confirm Pickup
                          </button>
                        </>
                      )}
                    </>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* ── ROUTE DETAILS ── */}
        <div className="bg-white rounded-lg border border-slate-200 p-5">
          <h2 className="font-heading font-semibold text-sm uppercase tracking-wider text-slate-500 mb-4">Route Details</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Field label="From PIN Code" required error={fromPincodeError}>
              <input value={fromPincode} onChange={(e) => handleFromPincodeChange(e.target.value)}
                placeholder="600001" maxLength={6}
                className={fromPincodeError ? inputErrorCls : inputCls} data-testid="from-pincode" />
            </Field>
            <Field label="From City" required>
              <input value={fromCity} onChange={(e) => handleFromCityChange(e.target.value)}
                placeholder="Origin city" className={inputCls} data-testid="from-city" />
            </Field>
            <Field label="To PIN Code" required error={toPincodeError}>
              <input value={toPincode} onChange={(e) => handleToPincodeChange(e.target.value)}
                placeholder="110001" maxLength={6}
                className={toPincodeError ? inputErrorCls : inputCls} data-testid="to-pincode" />
            </Field>
            <Field label="To City" required>
              <input value={toCity} onChange={(e) => handleToCityChange(e.target.value)}
                placeholder="Destination city" className={inputCls} data-testid="to-city" />
            </Field>
          </div>
          <p className="text-xs text-slate-400 mt-3">Tip: Enter pincode to auto-fill city, or enter city name to auto-fill pincode.</p>
        </div>

        {/* ── CONSIGNER / CONSIGNEE ── */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          <div className="bg-white rounded-lg border border-slate-200 p-5">
            <h2 className="font-heading font-semibold text-sm uppercase tracking-wider text-slate-500 mb-4">Consigner / Sender</h2>
            <div className="space-y-3">
              <Field label="Full Name" required>
                {selectedCustomer && (
                  <label className="flex items-center gap-2 mb-3 text-xs font-semibold text-slate-600 cursor-pointer">
                    <input type="checkbox" checked={senderIsBilling} disabled={receiverIsBilling}
                      onChange={(e) => setSenderIsBilling(e.target.checked)} data-testid="sender-is-billing-toggle" />
                    Sender = Billing Account <span className="font-normal text-slate-400">(fill from customer profile)</span>
                  </label>
                )}
                <input value={senderName} onChange={(e) => setSenderName(e.target.value)} placeholder="Sender's full name" className={inputCls} data-testid="sender-name" />
              </Field>
              <Field label="Phone" required>
                <div className="flex">
                  <select className="px-2 py-2 border border-r-0 border-slate-200 rounded-l-sm text-sm bg-slate-50 text-slate-600 focus:outline-none">
                    <option>India (+91)</option><option>USA (+1)</option><option>UAE (+971)</option>
                  </select>
                  <input value={senderPhone}
                    onChange={(e) => setSenderPhone(cleanPhone(e.target.value))}
                    onPaste={(e) => {
                      e.preventDefault();
                      setSenderPhone(cleanPhone(e.clipboardData.getData("text")));
                    }}
                    maxLength={10}
                    placeholder="9876543210"
                    className={senderPhone && senderPhone.length < 10
                      ? "flex-1 px-3 py-2 border border-red-300 rounded-r-sm text-sm focus:outline-none"
                      : "flex-1 px-3 py-2 border border-slate-200 rounded-r-sm text-sm focus:outline-none focus:border-brand-500"}
                    data-testid="sender-phone" />
                </div>
              </Field>
              <Field label="Full Address" required>
                {receiverIsBilling && (
                  <label className="flex items-center gap-2 mb-2 text-xs font-semibold text-slate-600 cursor-pointer">
                    <input type="checkbox" checked={senderSameAsPickup}
                      onChange={(e) => setSenderSameAsPickup(e.target.checked)}
                      data-testid="sender-same-as-pickup-toggle" />
                    Same as Pickup Address
                  </label>
                )}
                <textarea value={senderAddress} onChange={(e) => setSenderAddress(e.target.value)}
                  rows={3} placeholder="Complete address with landmark" className={`${inputCls} resize-none`} data-testid="sender-address" />
              </Field>
            </div>
          </div>

          <div className="bg-white rounded-lg border border-slate-200 p-5">
            <h2 className="font-heading font-semibold text-sm uppercase tracking-wider text-slate-500 mb-4">Consignee / Receiver</h2>
            <div className="space-y-3">
              {selectedCustomer && (
                <Field label="Saved Delivery Address">
                  <select value={deliveryLocation}
                    onChange={(e) => {
                      const val = e.target.value;
                      if (val === "__add_new__") {
                        setDeliveryLocation("");
                        setShowAddDeliveryLocation(true);
                        return;
                      }
                      setShowAddDeliveryLocation(false);
                      setDeliveryLocation(val);
                      const sel = deliveryLocationList.find(l => l.id === val);
                      if (sel) {
                        setReceiverName(sel.name || "");
                        setReceiverPhone(sel.contact_phone || "");
                        setReceiverAddress(sel.address || "");
                        setToCity(sel.city || "");
                        // Pincode locked to the saved address's own pincode,
                        // never freely retyped alongside it -- this is the
                        // actual fix for the wrong-pickup-location class of
                        // bug (order 17092655, Sep 17 2026): a free-typed
                        // pincode can silently drift from the real
                        // registered address it's meant to represent.
                        if (sel.pincode) { setToPincode(sel.pincode); setToPincodeError(""); }
                      }
                    }}
                    className={selectCls} data-testid="delivery-location-select">
                    <option value="">— Type a new address below, or select saved —</option>
                    {deliveryLocationList.map(l => (
                      <option key={l.id} value={l.id}>{l.code} - {l.name} ({l.city})</option>
                    ))}
                    <option value="__add_new__">+ Add New Address</option>
                  </select>

                  {showAddDeliveryLocation && (
                    <div className="bg-slate-50 border border-slate-200 rounded-lg p-3 mt-2" data-testid="add-delivery-location-form">
                      <h3 className="text-xs font-bold text-slate-700 mb-2">Save New Delivery Address</h3>
                      <div className="grid grid-cols-2 gap-2">
                        <Field label="Location Name" required>
                          <input value={newDeliveryLocation.name}
                            onChange={(e) => setNewDeliveryLocation({ ...newDeliveryLocation, name: e.target.value })}
                            placeholder="e.g. Mumbai Warehouse"
                            className={inputCls}
                            data-testid="new-delivery-location-name" />
                        </Field>
                        <Field label="Pincode" required>
                          <input value={newDeliveryLocation.pincode}
                            onChange={(e) => setNewDeliveryLocation({ ...newDeliveryLocation, pincode: e.target.value.replace(/\D/g, "").slice(0, 6) })}
                            placeholder="400056"
                            maxLength={6}
                            className={inputCls}
                            data-testid="new-delivery-location-pincode" />
                        </Field>
                        <Field label="City" required>
                          <input value={newDeliveryLocation.city}
                            onChange={(e) => setNewDeliveryLocation({ ...newDeliveryLocation, city: e.target.value })}
                            placeholder="Mumbai"
                            className={inputCls}
                            data-testid="new-delivery-location-city" />
                        </Field>
                        <Field label="Contact Phone">
                          <input value={newDeliveryLocation.contact_phone}
                            onChange={(e) => setNewDeliveryLocation({ ...newDeliveryLocation, contact_phone: cleanPhone(e.target.value) })}
                            placeholder="9876543210"
                            maxLength={10}
                            className={inputCls}
                            data-testid="new-delivery-location-phone" />
                        </Field>
                        <div className="col-span-2">
                          <Field label="Full Address" required>
                            <input value={newDeliveryLocation.address}
                              onChange={(e) => setNewDeliveryLocation({ ...newDeliveryLocation, address: e.target.value })}
                              placeholder="Street, landmark..."
                              className={inputCls}
                              data-testid="new-delivery-location-address" />
                          </Field>
                        </div>
                        <div className="col-span-2">
                          <Field label="Contact Person">
                            <input value={newDeliveryLocation.contact_person}
                              onChange={(e) => setNewDeliveryLocation({ ...newDeliveryLocation, contact_person: e.target.value })}
                              placeholder="Person name"
                              className={inputCls}
                              data-testid="new-delivery-location-contact-person" />
                          </Field>
                        </div>
                      </div>
                      <button
                        onClick={async () => {
                          if (!newDeliveryLocation.name || !newDeliveryLocation.address || !newDeliveryLocation.city || !newDeliveryLocation.pincode) {
                            toast.error("Name, address, city and pincode are required");
                            return;
                          }
                          setSavingDeliveryLocation(true);
                          try {
                            const res = await api.post(`/v2/delivery-locations/create`, {
                              owner_id: selectedCustomer.id,
                              owner_type: "CUSTOMER",
                              location_name: newDeliveryLocation.name,
                              contact_person: newDeliveryLocation.contact_person,
                              contact_mobile: newDeliveryLocation.contact_phone,
                              address_line1: newDeliveryLocation.address,
                              address_line2: "",
                              city: newDeliveryLocation.city,
                              pincode: newDeliveryLocation.pincode,
                              state: "",
                            });
                            // Auto-fill the receiver fields + select this new
                            // location immediately, same as picking an
                            // existing saved one -- staff shouldn't have to
                            // retype what they just entered.
                            setReceiverName(newDeliveryLocation.name);
                            setReceiverPhone(newDeliveryLocation.contact_phone);
                            setReceiverAddress(newDeliveryLocation.address);
                            setToCity(newDeliveryLocation.city);
                            setToPincode(newDeliveryLocation.pincode);
                            setToPincodeError("");
                            setDeliveryLocation(res.data.location_code);
                            setShowAddDeliveryLocation(false);
                            fetchCustomerDeliveryLocations(selectedCustomer.id);
                          } catch (err) {
                            toast.error(err.response?.data?.detail || "Failed to save address");
                          } finally {
                            setSavingDeliveryLocation(false);
                          }
                        }}
                        disabled={savingDeliveryLocation}
                        data-testid="save-new-delivery-location-btn"
                        className="mt-2 flex items-center gap-2 px-3 py-1.5 bg-brand-500 text-white text-xs font-semibold rounded-sm hover:bg-brand-600 disabled:opacity-50">
                        {savingDeliveryLocation
                          ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                          : <Plus className="w-3.5 h-3.5" />}
                        Save Address
                      </button>
                    </div>
                  )}
                </Field>
              )}
              <Field label="Full Name" required>
                <label className="flex items-center gap-2 mb-3 text-xs font-semibold text-slate-600 cursor-pointer">
                  <input type="checkbox" checked={receiverIsBilling}
                    onChange={(e) => setReceiverIsBilling(e.target.checked)}
                    data-testid="receiver-is-billing-toggle" />
                  Receiver = Billing Account
                </label>
                <input value={receiverName} onChange={(e) => setReceiverName(e.target.value)} placeholder="Receiver's full name" className={inputCls} data-testid="receiver-name" />
              </Field>
              <Field label="Phone" required>
                <div className="flex">
                  <select className="px-2 py-2 border border-r-0 border-slate-200 rounded-l-sm text-sm bg-slate-50 text-slate-600 focus:outline-none">
                    <option>India (+91)</option><option>USA (+1)</option><option>UAE (+971)</option>
                  </select>
                  <input value={receiverPhone}
                    onChange={(e) => setReceiverPhone(cleanPhone(e.target.value))}
                    onPaste={(e) => {
                      e.preventDefault();
                      setReceiverPhone(cleanPhone(e.clipboardData.getData("text")));
                    }}
                    maxLength={10}
                    placeholder="9876543210"
                    className={receiverPhone && receiverPhone.length < 10
                      ? "flex-1 px-3 py-2 border border-red-300 rounded-r-sm text-sm focus:outline-none"
                      : "flex-1 px-3 py-2 border border-slate-200 rounded-r-sm text-sm focus:outline-none focus:border-brand-500"}
                    data-testid="receiver-phone" />
                </div>
              </Field>
              <Field label="Full Address" required>
                <textarea value={receiverAddress} onChange={(e) => setReceiverAddress(e.target.value)}
                  rows={3} placeholder="Complete address with landmark" className={`${inputCls} resize-none`} data-testid="receiver-address" />
              </Field>
              {addrIssues.length > 0 && (
                <div className="space-y-1" data-testid="address-issues">
                  {addrIssues.map((i, n) => (
                    <div key={n} className={`text-xs rounded border px-2 py-1.5 ${i.level === "block" ? "bg-red-50 border-red-300 text-red-800 font-semibold" : "bg-amber-50 border-amber-300 text-amber-800"}`}>
                      {i.level === "block" ? "⛔ " : "⚠ "}{i.message}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* ── SELLER / VENDOR (collapsible) ── */}
        <div className="bg-white rounded-lg border border-slate-200">
          <button onClick={() => setShowSeller(!showSeller)} className="w-full flex items-center justify-between p-5 text-left">
            <h2 className="font-heading font-semibold text-sm uppercase tracking-wider text-slate-500">
              Seller / Vendor Details <span className="text-slate-400 normal-case text-xs">(optional)</span>
            </h2>
            {showSeller ? <ChevronUp className="w-4 h-4 text-slate-400" /> : <ChevronDown className="w-4 h-4 text-slate-400" />}
          </button>
          {showSeller && (
            <div className="px-5 pb-5 grid grid-cols-1 sm:grid-cols-3 gap-4 border-t border-slate-100">
              <Field label="Seller Name"><input value={sellerName} onChange={(e) => setSellerName(e.target.value)} placeholder="Vendor name" className={inputCls} data-testid="seller-name" /></Field>
              <Field label="Phone"><input value={sellerPhone} onChange={(e) => setSellerPhone(e.target.value)} placeholder="Phone number" className={inputCls} data-testid="seller-phone" /></Field>
              <Field label="GST Number"><input value={sellerGST} onChange={(e) => setSellerGST(e.target.value)} placeholder="GST number" className={inputCls} data-testid="seller-gst" /></Field>
              <Field label="Address"><input value={sellerAddress} onChange={(e) => setSellerAddress(e.target.value)} placeholder="Vendor address" className={inputCls} data-testid="seller-address" /></Field>
            </div>
          )}
        </div>

        {/* ── SHIPMENT DETAILS ── */}
        <div className="bg-white rounded-lg border border-slate-200 p-5">
          <h2 className="font-heading font-semibold text-sm uppercase tracking-wider text-slate-500 mb-4">Shipment Details</h2>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
            {/* Shipping Mode */}
            <div>
              <label className="block text-xs font-semibold text-slate-600 mb-2">Shipping Mode</label>
              <div className="flex gap-1">
                {[["surface", "Surface"], ["express", "Express"], ["ndd", "NDD"]].map(([val, lbl]) => (
                  <button key={val} onClick={() => setMode(val)} data-testid={`mode-${val}`}
                    className={`flex-1 py-1.5 text-xs font-semibold rounded-sm transition-colors ${mode === val ? "bg-brand-500 text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"}`}>{lbl}</button>
                ))}
              </div>
            </div>
            
            {/* Consignment Type */}
            <div>
              <label className="block text-xs font-semibold text-slate-600 mb-2">Consignment Type</label>
              <select value={consignmentType} onChange={(e) => setConsignmentType(e.target.value)} className={selectCls} data-testid="consignment-type">
                <option value="document">Document</option>
                <option value="non-document">Non-Document</option>
                <option value="others">Others</option>
              </select>
            </div>
            {/* Consignment Value */}
            <Field label="Consignment Value (₹)">
              <input type="number" value={consignmentValue} onChange={(e) => setConsignmentValue(e.target.value)}
                placeholder="0.00" className={inputCls} data-testid="consignment-value" />
            </Field>
            {/* Product Category */}
            <Field label="Product Category" required>
              <select value={productCategory} onChange={(e) => setProductCategory(e.target.value)} className={selectCls} data-testid="product-category">
                <option value="">— Select —</option>
                {productCategories.map(p => (
                  <option key={p.code} value={p.code || p.name}>{p.name}</option>
                ))}
              </select>
            </Field>
            
            {/* Connection Through */}
            <Field label="Connection Through">
              <select value={connectionThrough} onChange={(e) => setConnectionThrough(e.target.value)} className={selectCls} data-testid="connection-through">
                <option value="">— Select —</option>
                <option value="SENDTOO_NETWORK">Sendtoo Network</option>
                <option value="DELHIVERY_B2B">Delhivery B2B</option>
                <option value="DELHIVERY_C2C">Delhivery C2C</option>
                <option value="BLUEDART">Bluedart</option>
                <option value="INDIA_POST">India Post</option>
                <option value="OTHERS">Others</option>
              </select>
            </Field>
            {/* Material Description */}
            <div className="sm:col-span-2">
              <Field label="Material Description">
                <input value={materialDesc} onChange={(e) => setMaterialDesc(e.target.value)}
                  placeholder="Contents of the package" className={inputCls} data-testid="material-desc" />
              </Field>
            </div>
            {/* Initial Weight */}
            <Field label="Initial Weight (kg)">
              <input type="number" step="0.01" value={initialWeight} onChange={(e) => setInitialWeight(e.target.value)}
                placeholder="0.00" className={inputCls} data-testid="initial-weight" />
            </Field>
            {/* COD Amount */}
            {paymentMode === "cod" && (
              <Field label="COD Amount (₹)" required>
                <input type="number" value={codAmount} onChange={(e) => setCodAmount(e.target.value)}
                  placeholder="0.00" className={inputCls} data-testid="cod-amount" />
              </Field>
            )}
          </div>

          {/* Insurance Toggle */}
          <div className="mt-4 bg-blue-50 border border-blue-200 rounded-lg p-4">
            <div className="flex items-center justify-between flex-wrap gap-3">
              <div className="flex items-center gap-3 flex-wrap">
                <span className="text-sm font-semibold text-slate-700">Insurance</span>
                <span className={`text-xs px-2 py-0.5 rounded-full font-semibold ${insuranceType === "CARRIER_RISK" ? "bg-green-100 text-green-700" : "bg-amber-100 text-amber-700"}`}>
                  {insuranceType === "CARRIER_RISK" ? "Carrier Risk — Sendtoo Liable" : "Owner Risk — Customer Liable"}
                </span>
                {insuranceType === "CARRIER_RISK" && consignmentValue && (
                  <span className="text-xs text-blue-600 font-semibold">Premium: ₹{insurancePremium}</span>
                )}
              </div>
              <div className="flex rounded-sm border border-slate-200 overflow-hidden">
                <button onClick={() => setInsuranceType("CARRIER_RISK")}
                  className={`px-3 py-1.5 text-xs font-semibold transition-colors ${insuranceType === "CARRIER_RISK" ? "bg-green-500 text-white" : "bg-white text-slate-600 hover:bg-slate-50"}`}>
                  Opt In (Carrier Risk)
                </button>
                <button onClick={() => setInsuranceType("OWNER_RISK")}
                  className={`px-3 py-1.5 text-xs font-semibold transition-colors ${insuranceType === "OWNER_RISK" ? "bg-amber-500 text-white" : "bg-white text-slate-600 hover:bg-slate-50"}`}>
                  Opt Out (Owner Risk)
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* ── WEIGHT & DIMENSIONS ── */}
        <div className="bg-white rounded-lg border border-slate-200 p-5">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-heading font-semibold text-sm uppercase tracking-wider text-slate-500">Weight & Dimensions</h2>
            <div className="flex items-center gap-3">
              <label className="flex items-center gap-1.5 text-xs font-semibold text-slate-600 cursor-pointer">
                <input type="checkbox" checked={addLater} onChange={(e) => setAddLater(e.target.checked)} className="rounded" data-testid="add-later-checkbox" />
                Add later / Post pickup
              </label>
              <div className="flex rounded-sm border border-slate-200 overflow-hidden">
                {[["single", "Single Box"], ["multi", "Multi Box"]].map(([val, lbl]) => (
                  <button key={val} onClick={() => setBoxMode(val)} data-testid={`box-mode-${val}`}
                    className={`px-3 py-1.5 text-xs font-semibold transition-colors ${boxMode === val ? "bg-brand-500 text-white" : "bg-white text-slate-600 hover:bg-slate-50"}`}>{lbl}</button>
                ))}
              </div>
            </div>
          </div>

          {!addLater && (
            <>
              {/* Single Box */}
              {boxMode === "single" && (
                <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
                  <Field label="Dead Weight (kg)">
                    <input type="number" step="0.01" value={deadWeight} onChange={(e) => setDeadWeight(e.target.value)} placeholder="0.00" className={inputCls} data-testid="dead-weight" />
                  </Field>
                  <Field label="Length (cm)">
                    <input type="number" step="0.1" value={length} onChange={(e) => setLength(e.target.value)} placeholder="0" className={inputCls} data-testid="box-length" />
                  </Field>
                  <Field label="Breadth (cm)">
                    <input type="number" step="0.1" value={breadth} onChange={(e) => setBreadth(e.target.value)} placeholder="0" className={inputCls} data-testid="box-breadth" />
                  </Field>
                  <Field label="Height (cm)">
                    <input type="number" step="0.1" value={height} onChange={(e) => setHeight(e.target.value)} placeholder="0" className={inputCls} data-testid="box-height" />
                  </Field>
                  <Field label="Divisor">
                    <select value={divisor} onChange={(e) => setDivisor(Number(e.target.value))} className={selectCls} data-testid="divisor">
                      {DIVISORS.map(d => <option key={d} value={d}>{d}</option>)}
                    </select>
                  </Field>
                </div>
              )}

              {/* Multi Box */}
              {boxMode === "multi" && (
                <div>
                  {/* Same size toggle */}
                  <div className="flex items-center gap-3 mb-4">
                    <span className="text-xs font-semibold text-slate-600">Box entry mode:</span>
                    <div className="flex rounded-sm border border-slate-200 overflow-hidden">
                      {[["false", "Different Sizes"], ["true", "Same Size (All Boxes)"]].map(([val, lbl]) => (
                        <button key={val} onClick={() => setSameSize(val === "true")}
                          className={`px-3 py-1.5 text-xs font-semibold transition-colors ${String(sameSize) === val ? "bg-brand-500 text-white" : "bg-white text-slate-600 hover:bg-slate-50"}`}>{lbl}</button>
                      ))}
                    </div>
                  </div>

                  {/* Same Size entry */}
                  {sameSize ? (
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-3">
                      <div className="grid grid-cols-2 sm:grid-cols-6 gap-3">
                        <Field label="Total Boxes">
                          <input type="number" min="1" value={totalBoxCount}
                            onChange={(e) => setTotalBoxCount(parseInt(e.target.value) || 1)} className={inputCls} />
                        </Field>
                        <Field label="L (cm)">
                          <input type="number" value={sameSizeData.l} onChange={(e) => setSameSizeData({ ...sameSizeData, l: e.target.value })} placeholder="0" className={inputCls} />
                        </Field>
                        <Field label="B (cm)">
                          <input type="number" value={sameSizeData.b} onChange={(e) => setSameSizeData({ ...sameSizeData, b: e.target.value })} placeholder="0" className={inputCls} />
                        </Field>
                        <Field label="H (cm)">
                          <input type="number" value={sameSizeData.h} onChange={(e) => setSameSizeData({ ...sameSizeData, h: e.target.value })} placeholder="0" className={inputCls} />
                        </Field>
                        <Field label="Dead Wt/box (kg)">
                          <input type="number" value={sameSizeData.deadWt} onChange={(e) => setSameSizeData({ ...sameSizeData, deadWt: e.target.value })} placeholder="0" className={inputCls} />
                        </Field>
                        <Field label="Divisor">
                          <select value={sameSizeData.divisor} onChange={(e) => setSameSizeData({ ...sameSizeData, divisor: Number(e.target.value) })} className={selectCls}>
                            {DIVISORS.map(d => <option key={d} value={d}>{d}</option>)}
                          </select>
                        </Field>
                      </div>
                      <p className="text-xs text-blue-700 font-semibold mt-3">
                        {totalBoxCount} boxes × same dimensions = {totalBoxCount} AWBs will be generated
                      </p>
                    </div>
                  ) : (
                    /* Different sizes table */
                    <div>
                      <div className="overflow-x-auto">
                        <table className="w-full min-w-[600px] text-xs">
                          <thead className="bg-slate-50">
                            <tr>
                              {["#", "Boxes", "L (cm)", "B (cm)", "H (cm)", "Divisor", "Dead Wt", "Vol Wt", "Chargeable", ""].map(h => (
                                <th key={h} className="px-2 py-2 text-left font-bold text-slate-500 uppercase">{h}</th>
                              ))}
                            </tr>
                          </thead>
                          <tbody>
                            {boxes.map((box, idx) => {
                              const vw = calcVolWt(box.l, box.b, box.h, box.qty, box.divisor);
                              const dw = (parseFloat(box.deadWt) || 0) * (parseInt(box.qty) || 1);
                              const cw = Math.max(dw, vw);
                              return (
                                <tr key={box.id} className="border-t border-slate-100">
                                  <td className="px-2 py-1.5 font-semibold text-slate-500">{idx + 1}</td>
                                  <td className="px-2 py-1.5"><input type="number" min="1" value={box.qty} onChange={(e) => updateBox(box.id, "qty", e.target.value)} className="w-16 px-2 py-1 border border-slate-200 rounded text-xs" /></td>
                                  {["l", "b", "h"].map(f => (
                                    <td key={f} className="px-2 py-1.5"><input type="number" step="0.1" value={box[f]} onChange={(e) => updateBox(box.id, f, e.target.value)} placeholder="0" className="w-16 px-2 py-1 border border-slate-200 rounded text-xs" /></td>
                                  ))}
                                  <td className="px-2 py-1.5">
                                    <select value={box.divisor} onChange={(e) => updateBox(box.id, "divisor", Number(e.target.value))} className="w-16 px-1 py-1 border border-slate-200 rounded text-xs bg-white">
                                      {DIVISORS.map(d => <option key={d} value={d}>{d}</option>)}
                                    </select>
                                  </td>
                                  <td className="px-2 py-1.5"><input type="number" step="0.01" value={box.deadWt} onChange={(e) => updateBox(box.id, "deadWt", e.target.value)} placeholder="0" className="w-16 px-2 py-1 border border-slate-200 rounded text-xs" /></td>
                                  <td className="px-2 py-1.5 font-semibold text-indigo-600">{vw.toFixed(2)}</td>
                                  <td className="px-2 py-1.5 font-bold text-brand-500">{cw.toFixed(2)}</td>
                                  <td className="px-2 py-1.5">
                                    <button onClick={() => removeBox(box.id)} className="text-slate-300 hover:text-red-500"><Trash2 className="w-3.5 h-3.5" /></button>
                                  </td>
                                </tr>
                              );
                            })}
                            <tr className="border-t-2 border-slate-300 bg-slate-50 font-bold text-xs">
                              <td colSpan={6} className="px-2 py-2 text-right text-slate-600">Totals:</td>
                              <td className="px-2 py-2 text-slate-700">{multiTotalDeadWt.toFixed(2)}</td>
                              <td className="px-2 py-2 text-indigo-600">{multiTotalVolWt.toFixed(2)}</td>
                              <td className="px-2 py-2 text-brand-500">{multiChargeableWt.toFixed(2)}</td>
                              <td />
                            </tr>
                          </tbody>
                        </table>
                      </div>
                      <button onClick={addBox} className="mt-3 flex items-center gap-1.5 text-xs font-semibold text-brand-500 hover:text-brand-600 px-2 py-1 rounded hover:bg-brand-50 transition-colors" data-testid="add-box-btn">
                        <Plus className="w-3.5 h-3.5" /> Add Box Row
                      </button>
                    </div>
                  )}
                </div>
              )}

              {/* Weight summary */}
              {finalChargeableWt > 0 && (
                <div className="mt-4 grid grid-cols-3 gap-3 bg-slate-50 rounded-lg p-3">
                  <div className="text-center">
                    <p className="text-xs text-slate-400 font-semibold uppercase">Volumetric Wt</p>
                    <p className="font-heading font-bold text-indigo-600">{(boxMode === "single" ? singleVolWt : sameSize ? sameSizeVolWt : multiTotalVolWt).toFixed(2)} kg</p>
                  </div>
                  <div className="text-center">
                    <p className="text-xs text-slate-400 font-semibold uppercase">Dead Weight</p>
                    <p className="font-heading font-bold text-slate-700">{(boxMode === "single" ? singleDeadWt : sameSize ? sameSizeDeadWt : multiTotalDeadWt).toFixed(2)} kg</p>
                  </div>
                  <div className="text-center">
                    <p className="text-xs text-slate-400 font-semibold uppercase">Chargeable Wt</p>
                    <p className="font-heading font-bold text-brand-500">{finalChargeableWt.toFixed(2)} kg</p>
                  </div>
                </div>
              )}
            </>
          )}

          {addLater && (
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 text-sm text-amber-700">
              Using initial weight: <strong>{initialWeight || "0"} kg</strong>. Exact dimensions will be updated after pickup.
            </div>
          )}

          {/* AWB count badge */}
          {boxMode === "multi" && (
            <div className="mt-3 bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm">
              <span className="font-semibold text-amber-800">AWBs to be generated: {totalAwbCount}</span>
              <span className="text-amber-600 ml-2 text-xs">(one label per physical box)</span>
            </div>
          )}
        </div>

        {/* ── SHIPMENT PHOTOS ── */}
        <div className="bg-white rounded-lg border border-slate-200 p-5">
          <h2 className="font-heading font-semibold text-sm uppercase tracking-wider text-slate-500 mb-4">Shipment Photos</h2>
          <div className="flex gap-3 flex-wrap">
            <button onClick={() => photoRef.current?.click()}
              className="flex items-center gap-2 px-4 py-2 border-2 border-dashed border-slate-300 rounded-lg text-sm font-semibold text-slate-500 hover:border-brand-400 hover:text-brand-500 transition-colors">
              <Upload className="w-4 h-4" /> Upload Photos
            </button>
            <input ref={photoRef} type="file" accept="image/*" multiple onChange={handlePhotoUpload} className="hidden" data-testid="photo-upload" />
            {photos.map((p, i) => (
              <div key={i} className="relative w-16 h-16 rounded-lg overflow-hidden border border-slate-200">
                <img src={p.url} alt={p.name} className="w-full h-full object-cover" />
                <button onClick={() => setPhotos(photos.filter((_, j) => j !== i))}
                  className="absolute top-0.5 right-0.5 w-4 h-4 bg-red-500 rounded-full flex items-center justify-center">
                  <X className="w-2.5 h-2.5 text-white" />
                </button>
              </div>
            ))}
          </div>
          {photos.length === 0 && <p className="text-xs text-slate-400 mt-2">Upload package photos (JPG, PNG, WEBP)</p>}
        </div>
        {/* ── INVOICE + E-WAY BILL (kept together) ── */}
        {(parseFloat(finalChargeableWt) >= 20 || parseFloat(consignmentValue) > 50000) && (
          <div className="bg-white rounded-lg border border-slate-200 p-5" data-testid="invoice-eway-section">
            <h2 className="font-heading font-semibold text-sm uppercase tracking-wider text-slate-500 mb-1">
              Invoice {requiresEwayBill ? "& E-way Bill" : "/ Delivery Challan"} <span className="text-red-500">*</span>
            </h2>
            <p className="text-xs text-slate-400 mb-3">Mandatory for shipments ≥20kg or above ₹50,000 in value{requiresEwayBill ? " - the E-way bill number is also mandatory above ₹50,000" : ""}</p>
            <div className={`grid gap-4 ${requiresEwayBill ? "md:grid-cols-2" : "grid-cols-1"}`}>
              <div className="border border-slate-200 rounded-lg p-3 space-y-3">
                <p className="text-xs font-bold text-slate-600 uppercase">Invoice</p>
                <Field label="Invoice Number" required={invoiceMode !== "generate"}>
                  <input value={invoiceNumber} onChange={(e) => setInvoiceNumber(e.target.value.slice(0, 40))} placeholder="Number on the customer's invoice"
                    className={invoiceMode === "generate" || invoiceNumber.trim() ? inputCls : inputErrorCls} data-testid="invoice-number" />
                </Field>
                <div className="flex gap-3 flex-wrap items-center">
                  <button onClick={() => invoiceRef.current?.click()}
                    className={`flex items-center gap-2 px-4 py-2 border-2 border-dashed rounded-lg text-sm font-semibold transition-colors ${invoiceFile ? "border-emerald-400 text-emerald-600" : "border-slate-300 text-slate-500 hover:border-brand-400 hover:text-brand-500"}`}>
                    <Upload className="w-4 h-4" /> {invoiceFile ? invoiceFile.name : "Upload Invoice"}
                  </button>
                  <input ref={invoiceRef} type="file" accept="image/*,application/pdf"
                    onChange={(e) => { setInvoiceFile(e.target.files?.[0] || null); setInvoiceMode(""); }}
                    className="hidden" data-testid="invoice-upload" />
                  <span className="text-xs text-slate-400">or</span>
                  <button onClick={() => { setInvoiceMode("generate"); setInvoiceFile(null); }}
                    className={`px-4 py-2 rounded-lg text-sm font-semibold border-2 transition-colors ${invoiceMode === "generate" ? "border-emerald-400 bg-emerald-50 text-emerald-700" : "border-slate-300 text-slate-500 hover:border-brand-400"}`}>
                    {invoiceMode === "generate" ? "✓ Will generate Delivery Challan" : "Generate Delivery Challan instead"}
                  </button>
                </div>
              </div>

              {requiresEwayBill && (
                <div className="border border-amber-300 bg-amber-50 rounded-lg p-3 space-y-3" data-testid="eway-section">
                  <p className="text-xs font-bold text-amber-800 uppercase flex items-center gap-1"><AlertTriangle className="w-3.5 h-3.5" /> E-way Bill</p>
                  <Field label="E-way Bill Number" required>
                    <input value={ewayBill} onChange={(e) => setEwayBill(e.target.value.replace(/\D/g, "").slice(0, 12))}
                      placeholder="12-digit E-way Bill Number" maxLength={12}
                      className={ewayBill.length === 12 ? inputCls : inputErrorCls} data-testid="eway-bill-number" />
                    {ewayBill && ewayBill.length !== 12 && <p className="text-xs text-red-600 mt-1">E-way Bill must be exactly 12 digits</p>}
                  </Field>
                  <div className="flex flex-wrap items-center gap-3">
                    <button type="button" onClick={() => ewayRef.current?.click()} data-testid="eway-bill-upload-btn"
                      className={`flex items-center gap-2 px-4 py-2 border-2 border-dashed rounded-lg text-sm font-semibold ${ewayFile ? "border-emerald-400 text-emerald-600" : "border-amber-400 text-amber-800 hover:bg-amber-100"}`}>
                      <Upload className="w-4 h-4" /> {ewayFile ? ewayFile.name : "Upload E-way Bill (PDF)"}
                    </button>
                    {ewayFile && <button type="button" onClick={() => { setEwayFile(null); if (ewayRef.current) ewayRef.current.value = ""; }} className="text-xs text-red-600 underline">remove</button>}
                    <input ref={ewayRef} type="file" accept="application/pdf,image/*" className="hidden" data-testid="eway-bill-file"
                      onChange={(e) => { const f = e.target.files?.[0] || null; if (f && f.size > 8 * 1024 * 1024) { toast.error("E-way bill file must be under 8 MB"); return; } setEwayFile(f); }} />
                  </div>
                  <p className="text-xs text-amber-700">💡 Select the appropriate Transporter ID (carrier handling this shipment) and generate Part-A of the E-way Bill on the government portal.</p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* ── BILLING DETAILS ── */}
        <div className="bg-white rounded-lg border border-slate-200 p-5">
          <h2 className="font-heading font-semibold text-sm uppercase tracking-wider text-slate-500 mb-4">Billing Details</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 items-end">
            <div>
              <label className="block text-xs font-semibold text-slate-600 mb-1.5">Final Chargeable Weight</label>
              <div className="px-3 py-2 bg-slate-50 border border-slate-200 rounded-sm text-sm font-heading font-bold text-brand-500">
                {finalChargeableWt.toFixed(2)} kg
              </div>
            </div>
            <div>
              <label className="block text-xs font-semibold text-slate-600 mb-1.5">Auto-Calculated Bill</label>
              <div className="px-3 py-2 bg-slate-50 border border-slate-200 rounded-sm text-sm font-heading font-bold text-slate-700">
                {estimatedPrice ? `₹${estimatedPrice}` : "Calculating..."}
              </div>
            </div>
            <Field label="Bill Amount (Manual Override)">
              <input type="number" value={billOverride} onChange={(e) => setBillOverride(e.target.value)}
                placeholder="Override amount" className={`${inputCls} border-dashed`} data-testid="bill-override" />
            </Field>
            <div>
              <label className="block text-xs font-semibold text-slate-600 mb-1.5">Final Bill Amount</label>
              <div className={`px-3 py-2 border rounded-sm text-sm font-heading font-bold ${billOverride ? "bg-amber-50 border-amber-300 text-amber-700" : "bg-green-50 border-green-300 text-green-700"}`}>
                ₹{finalBillAmount || 0}
                {billOverride && <span className="text-xs font-normal ml-1">(Override)</span>}
              </div>
            </div>
          </div>
        </div>

        {/* ── PAYMENT MODE (mandatory, final step) ── */}
            <div className="bg-white rounded-lg border border-slate-200 p-5">
              <h2 className="font-heading font-semibold text-sm uppercase tracking-wider text-slate-500 mb-1">
                Payment Mode <span className="text-red-500">*</span>
              </h2>
              <p className="text-xs text-slate-400 mb-3">Select how this shipment will be paid for</p>
              <div className="flex gap-2 mb-3">
                <button type="button"
                  onClick={() => { setPaymentTiming("now"); setPaymentMode(""); }}
                  data-testid="payment-timing-now"
                  className={`px-3 py-1.5 rounded-md text-xs font-semibold border-2 transition-colors ${
                    paymentTiming === "now" ? "border-brand-500 bg-brand-50 text-brand-700"
                    : "border-slate-200 bg-white text-slate-600 hover:border-slate-300"
                  }`}>
                  Pay Now
                </button>
                <button type="button"
                  onClick={() => { setPaymentTiming("ppp"); setPaymentMode(""); }}
                  data-testid="payment-timing-ppp"
                  className={`px-3 py-1.5 rounded-md text-xs font-semibold border-2 transition-colors ${
                    paymentTiming === "ppp" ? "border-purple-500 bg-purple-50 text-purple-700"
                    : "border-slate-200 bg-white text-slate-600 hover:border-slate-300"
                  }`}>
                  ⏳ Pay Post Pickup
                </button>
              </div>
              {paymentTiming === "now" && (
                <div className="flex gap-2 flex-wrap">
                  {!(selectedCustomer?.customer_segment === "WALKIN" || selectedCustomer?.email?.endsWith("@walkin.sendtoo.in")) && (
                    <button type="button"
                      onClick={() => setPaymentMode(customerWallet?.credit_enabled ? "credit" : "prepaid")}
                      data-testid="payment-mode-wallet-credit"
                      className={`px-3 py-1.5 rounded-md text-xs font-semibold border-2 transition-colors ${
                        (paymentMode === "credit" || (paymentMode === "prepaid" && customerWallet?.credit_enabled === false))
                          ? "border-emerald-500 bg-emerald-50 text-emerald-700"
                          : "border-slate-200 bg-white text-slate-600 hover:border-slate-300"
                      }`}>
                      {customerWallet?.credit_enabled ? "🧾 Credit" : "💰 Wallet"} ₹{estimatedPrice || 0}
                    </button>
                  )}
                  <button type="button"
                    onClick={() => setPaymentMode(paymentMode === "cash" ? "" : "cash")}
                    data-testid="payment-mode-cash"
                    className={`px-3 py-1.5 rounded-md text-xs font-semibold border-2 transition-colors ${
                      paymentMode === "cash" ? "border-amber-500 bg-amber-50 text-amber-700"
                      : "border-slate-200 bg-white text-slate-600 hover:border-slate-300"
                    }`}>
                    💵 Cash ₹{estimatedPrice || 0}
                  </button>
                  <button type="button"
                    onClick={() => setPaymentMode(paymentMode === "prepaid" ? "" : "prepaid")}
                    data-testid="payment-mode-upi"
                    className={`px-3 py-1.5 rounded-md text-xs font-semibold border-2 transition-colors ${
                      paymentMode === "prepaid" ? "border-blue-500 bg-blue-50 text-blue-700"
                      : "border-slate-200 bg-white text-slate-600 hover:border-slate-300"
                    }`}>
                    💳 UPI ₹{estimatedPrice || 0}
                  </button>
                </div>
              )}
              {paymentTiming === "ppp" && (
                <div className="flex gap-2 flex-wrap">
                  <button type="button"
                    onClick={() => setPaymentMode(paymentMode === "ppp_cash" ? "" : "ppp_cash")}
                    data-testid="payment-mode-ppp-cash"
                    className={`px-3 py-1.5 rounded-md text-xs font-semibold border-2 transition-colors ${
                      paymentMode === "ppp_cash" ? "border-amber-500 bg-amber-50 text-amber-700"
                      : "border-slate-200 bg-white text-slate-600 hover:border-slate-300"
                    }`}>
                    💵 Cash (Post Pickup) ₹{estimatedPrice || 0}
                  </button>
                  <button type="button"
                    onClick={() => setPaymentMode(paymentMode === "ppp_upi" ? "" : "ppp_upi")}
                    data-testid="payment-mode-ppp-upi"
                    className={`px-3 py-1.5 rounded-md text-xs font-semibold border-2 transition-colors ${
                      paymentMode === "ppp_upi" ? "border-blue-500 bg-blue-50 text-blue-700"
                      : "border-slate-200 bg-white text-slate-600 hover:border-slate-300"
                    }`}>
                    💳 UPI (Post Pickup) ₹{estimatedPrice || 0}
                  </button>
                  <p className="w-full text-xs text-purple-600 bg-purple-50 px-3 py-2 rounded mt-1">
                    Shipment will connect without waiting for payment. Staff can mark it paid via Cash or UPI at any time, before or after connecting.
                  </p>
                </div>
              )}
            </div>
        {/* ── ACTION BUTTONS ── */}
        <div className="flex justify-end gap-3 pt-2">
          <button onClick={handleReset} data-testid="booking-reset-btn"
            className="flex items-center gap-2 px-6 py-2.5 border border-slate-200 text-slate-600 text-sm font-semibold rounded-sm hover:bg-slate-50 uppercase">
            <RefreshCw className="w-4 h-4" /> Reset Form
          </button>
          <button onClick={handleSubmit} disabled={loading} data-testid="booking-console-submit"
            className="flex items-center gap-2 px-8 py-2.5 bg-brand-500 text-white text-sm font-semibold rounded-sm hover:bg-brand-600 disabled:opacity-70 uppercase shadow-sm">
            <Send className="w-4 h-4" /> {loading ? "Submitting..." : "Submit Order"}
          </button>
        </div>
      </div>

      {/* ── LOCATION MISMATCH MODAL ── */}
      {showLocationWarning && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl p-6 max-w-sm w-full shadow-2xl">
            <div className="flex items-start gap-3 mb-4">
              <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center flex-shrink-0">
                <AlertTriangle className="w-5 h-5 text-amber-600" />
              </div>
              <div>
                <h3 className="font-bold text-slate-800">Location Mismatch</h3>
                <p className="text-sm text-slate-500 mt-1">
                  This pickup location is not registered under <strong>{selectedCustomer?.name}</strong>. Proceed anyway?
                </p>
              </div>
            </div>
            <div className="flex gap-3 justify-end">
              <button onClick={() => { setShowLocationWarning(false); setPendingWarehouse(""); }}
                className="px-4 py-2 text-sm font-semibold text-slate-600 border border-slate-200 rounded-lg hover:bg-slate-50">
                Cancel
              </button>
              <button onClick={() => { setWarehouse(pendingWarehouse); setShowLocationWarning(false); setPendingWarehouse(""); }}
                className="px-4 py-2 text-sm font-semibold text-white bg-amber-500 rounded-lg hover:bg-amber-600">
                Proceed Anyway
              </button>
            </div>
          </div>
        </div>
      )}
    {showC2cUpgradeWarning && (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg p-6 max-w-sm w-full mx-4">
          <h3 className="text-base font-bold text-slate-800 mb-2">Convert to Delhivery B2B?</h3>
          <p className="text-sm text-slate-600 mb-5">
            This shipment is {finalChargeableWt}kg on Delhivery C2C. Convert to B2B, or continue with C2C (max 50kg)?
          </p>
          <div className="flex gap-3">
            <button
              onClick={() => { setConnectionThrough("DELHIVERY_B2B"); setShowC2cUpgradeWarning(false); }}
              data-testid="c2c-upgrade-yes"
              className="flex-1 px-4 py-2.5 bg-brand-500 text-white text-sm font-semibold rounded-lg hover:bg-brand-600"
            >
              Yes, use B2B
            </button>
            <button
              onClick={() => setShowC2cUpgradeWarning(false)}
              data-testid="c2c-upgrade-no"
              className="flex-1 px-4 py-2.5 border border-slate-200 text-slate-600 text-sm font-semibold rounded-lg hover:bg-slate-50"
            >
              No, keep C2C
            </button>
          </div>
        </div>
      </div>
    )}
    {bookingSuccess && (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg p-6 max-w-sm w-full mx-4 text-center">
          <div className="text-emerald-600 text-3xl mb-2">✓</div>
          <h3 className="text-base font-bold text-slate-800 mb-1">Order Booked!</h3>
          <p className="text-sm text-slate-500 mb-5">
            AWB: {bookingSuccess.orderId}
            {bookingSuccess.awbCount > 1 ? ` (${bookingSuccess.awbCount} labels)` : ""}
          </p>
          <button
            onClick={resetForNextShipment}
            data-testid="book-another-btn"
            className="w-full px-4 py-2.5 bg-brand-500 text-white text-sm font-semibold rounded-lg hover:bg-brand-600 mb-2"
          >
            Book Another Shipment{selectedCustomer ? ` for ${selectedCustomer.name || "this customer"}` : ""}
          </button>
          <button
            onClick={() => navigate("/staff/orders")}
            data-testid="booking-done-btn"
            className="w-full px-4 py-2.5 text-slate-500 text-sm font-semibold hover:text-slate-700"
          >
            Done
          </button>
        </div>
      </div>
    )}
    {qrModal && (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg p-4 max-w-md w-full mx-4">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-bold text-slate-700">Collect Payment — Order {qrModal.orderId}</h3>
            <span className="text-xs text-slate-400 animate-pulse">● Waiting for payment...</span>
          </div>
          <iframe
            src={qrModal.url}
            title="Cashfree Payment"
            className="w-full rounded-lg border border-slate-200"
            style={{ height: "480px" }}
          />
          <button onClick={() => { navigator.clipboard.writeText(qrModal.url); toast.success("Link copied"); }}
            className="w-full mt-3 px-4 py-2 bg-slate-100 text-slate-700 text-sm font-semibold rounded-lg hover:bg-slate-200">
            Copy Link
          </button>
          <button onClick={() => finishBooking(qrModal.orderId, qrModal.awbCount)}
            className="w-full mt-2 px-4 py-2 text-slate-500 text-sm font-semibold hover:text-slate-700">
            Skip / Pay Later
          </button>
        </div>
      </div>
    )}
    </DashboardLayout>
  );
}

